import { StreamSfuClient } from './StreamSfuClient';
import { CallApi } from './gen/coordinator/video/CallApi';
import type { VideoApi } from './gen/coordinator/video/VideoApi';
import type { StreamResponse } from './coordinator/connection/api-client';
import { SfuJoinError } from './errors';
import { FastReconnectRejectedError } from './errors/FastReconnectRejectedError';
import {
  BasePeerConnectionOpts,
  type CallMediaEngine,
  Dispatcher,
  getCallMediaEngineProvider,
  getGenericSdp,
  isAudioTrackType,
  isSfuEvent,
  muteTypeToTrackType,
  NegotiationError,
  Publisher,
  ReconnectReason,
  Subscriber,
  toRtcConfiguration,
  TrackPublishOptions,
  trackTypeToParticipantStreamKey,
} from './rtc';
import type { E2EEManager } from './rtc/e2ee/E2EEManager';
import {
  registerEventHandlers,
  registerRingingCallEventHandlers,
} from './events/callEventHandlers';
import { CallingState, CallState, ClientState } from './store';
import {
  createSafeAsyncSubscription,
  createSubscription,
  getCurrentValue,
} from './store/rxUtils';
import { ScopedLogger, videoLoggerSystem } from './logger';
import {
  CallRingEvent,
  CallSettingsResponse,
  CallStateResponseFields,
  CollectUserFeedbackRequest,
  CollectUserFeedbackResponse,
  Credentials,
  DeleteCallRequest,
  GetCallResponse,
  GetCallSessionParticipantStatsDetailsResponse,
  GetOrCreateCallRequest,
  GoLiveRequest,
  JoinCallResponse,
  BlockUserRequest,
  KickUserRequest,
  UnblockUserRequest,
  PinRequest,
  QueryCallMembersRequest,
  QueryCallParticipantsRequest,
  QueryCallSessionParticipantStatsResponse,
  QueryCallSessionParticipantStatsTimelineResponse,
  QueryCallStatsMapResponse,
  RequestPermissionRequest,
  RequestPermissionResponse,
  RingCallRequest,
  SendVideoReactionRequest,
  StartClosedCaptionsRequest,
  StartFrameRecordingRequest,
  StartRTMPBroadcastsRequest,
  StartRecordingRequest,
  StartTranscriptionRequest,
  StatsOptions,
  StopClosedCaptionsRequest,
  StopLiveRequest,
  StopTranscriptionRequest,
  UnpinRequest,
  UpdateCallMembersRequest,
  UpdateCallRequest,
  UpdateUserPermissionsRequest,
} from './gen/coordinator';
import { OwnCapability } from './gen/coordinator';
import {
  AudioTrackType,
  CallConstructor,
  CallLeaveOptions,
  CallRecordingType,
  ClientPublishOptions,
  ClosedCaptionsSettings,
  JoinCallData,
  TrackMuteType,
  VideoTrackType,
} from './types';
import { BehaviorSubject, Subject, takeWhile } from 'rxjs';
import { ReconnectDetails } from './gen/video/sfu/event/events';
import {
  ClientCapability,
  ClientDetails,
  Codec,
  ErrorCode,
  ParticipantSource,
  PeerType,
  PublishOption,
  SubscribeOption,
  TrackType,
  VideoDimension,
  WebsocketReconnectStrategy,
} from './gen/video/sfu/models/models';
import {
  createStatsReporter,
  getSdkSignature,
  SfuStatsReporter,
  StatsReporter,
  Tracer,
} from './stats';
import type { ClientEventReporter, JoinReason, JoinSource } from './reporting';
import { AudioBindingsWatchdog } from './helpers/AudioBindingsWatchdog';
import { BlockedAudioTracker } from './helpers/BlockedAudioTracker';
import { TrackSubscriptionManager } from './helpers/TrackSubscriptionManager';
import { DynascaleManager } from './helpers/DynascaleManager';
import { createFirstVideoFrameDetector } from './helpers/firstVideoFrame';
import { createInboundAudioStallDetector } from './helpers/inboundAudioStallDetector';
import { ViewportTracker } from './helpers/ViewportTracker';
import { tracePageLifecycle } from './helpers/pageLifecycle';
import {
  CallOfflineState,
  NetworkReachabilityChecker,
} from './helpers/networkReachability';
import { isBrowserOffline } from './helpers/offlineRechecks';
import { PermissionsContext } from './permissions';
import { RingStatePoller, RingTimeout, resolveOwnRingOutcome } from './ringing';
import { CallTypes } from './CallType';
import { StreamClient } from './coordinator/connection/client';
import { retryInterval, sleep } from './coordinator/connection/utils';
import { SlidingWindowRateLimiter } from './helpers/SlidingWindowRateLimiter';
import {
  AllCallEvents,
  CallEventListener,
  ErrorFromResponse,
  RejectReason,
  StreamCallEvent,
} from './coordinator/connection/types';
import { getClientDetails } from './helpers/client-details';
import {
  CameraManager,
  getRNDefaultAudioDevice,
  MicrophoneManager,
  ScreenShareManager,
  SpeakerManager,
} from './devices';
import { normalize } from './devices/devicePersistence';
import {
  hasPending,
  settled,
  singleFlight,
  withoutConcurrency,
} from './helpers/concurrency';
import { ensureExhausted } from './helpers/ensureExhausted';
import { pushToIfMissing } from './helpers/array';
import { makeSafePromise } from './helpers/promise';
import { isReactNative } from './helpers/platforms';

/**
 * An object representation of a `Call`.
 */
export class Call {
  /**
   * The call type.
   */
  readonly type: string;

  /**
   * The call ID.
   */
  readonly id: string;

  /**
   * The call CID.
   */
  readonly cid: string;

  /**
   * The generated API for this call.
   */
  readonly api: CallApi;

  protected readonly videoApi: VideoApi;

  /**
   * The state of this call.
   */
  readonly state = new CallState();

  /**
   * Flag indicating whether this call is "watched" and receives
   * updates from the backend.
   */
  watching: boolean;

  /**
   * Device manager for the camera
   */
  readonly camera: CameraManager;

  /**
   * Device manager for the microphone.
   */
  readonly microphone: MicrophoneManager;

  /**
   * Device manager for the speaker.
   */
  readonly speaker: SpeakerManager;

  /**
   * Device manager for the screen.
   */
  readonly screenShare: ScreenShareManager;

  /**
   * The DynascaleManager instance.
   */
  readonly dynascaleManager: DynascaleManager | undefined;

  /**
   * Tracks viewport visibility for participant video elements.
   * Available only in DOM environments.
   */
  readonly viewportTracker: ViewportTracker | undefined;

  /**
   * Owns the SFU-side video-subscription state (per-session and global overrides).
   */
  readonly trackSubscriptionManager: TrackSubscriptionManager;

  /**
   * Warns periodically when a remote participant is publishing audio, but no
   * `<audio>` element has been bound for them.
   */
  readonly audioBindingsWatchdog: AudioBindingsWatchdog | undefined;

  /**
   * Tracks audio elements blocked by the browser's autoplay policy.
   * Subscribe to `blockedAudioTracker.autoplayBlocked$` to react to the
   * blocked state, and call {@link Call.resumeAudio} inside a user gesture
   * to retry playback.
   */
  readonly blockedAudioTracker: BlockedAudioTracker;

  subscriber?: Subscriber;
  publisher?: Publisher;
  e2eeManager?: E2EEManager;

  /**
   * Flag telling whether this call is a "ringing" call.
   */
  private readonly ringingSubject: Subject<boolean>;

  /**
   * The permissions context of this call.
   */
  readonly permissionsContext = new PermissionsContext();
  readonly tracer = new Tracer(null);
  readonly logger: ScopedLogger;

  /**
   * The event dispatcher instance dedicated to this Call instance.
   * @private
   */
  private readonly dispatcher = new Dispatcher();

  private clientPublishOptions?: ClientPublishOptions;
  private currentPublishOptions?: PublishOption[];
  private statsReportingIntervalInMs: number = 2000;
  private statsReporter?: StatsReporter;
  private stopInboundAudioStallDetector?: () => void;
  private fastReconnectOffer?: Promise<boolean>;
  private sfuStatsReporter?: SfuStatsReporter;
  private lastStatsOptions?: StatsOptions;
  private ringTimeout: RingTimeout | undefined;
  private ringStatePoller: RingStatePoller | undefined;

  private readonly clientState: ClientState;
  public readonly streamClient: StreamClient;
  public readonly clientEventReporter: ClientEventReporter;
  private sfuClient?: StreamSfuClient;
  private sfuClientTag = 0;
  private unifiedSessionId?: string;

  private readonly reconnectConcurrencyTag = Symbol('reconnectConcurrencyTag');
  private reconnectAttempts = 0;
  private reconnectStrategy = WebsocketReconnectStrategy.UNSPECIFIED;
  private reconnectReason = '';
  private fastReconnectDeadlineSeconds: number = 0;
  private disconnectionTimeoutSeconds: number = 0;
  private readonly offlineState: CallOfflineState;

  // (10 attempts per rolling 120 s window).
  private readonly rejoinRateLimiter = new SlidingWindowRateLimiter(10, 120000);
  // Backstop for slow REJOIN loops that stay under the short window
  // (20 attempts per rolling 30 min window). Dropped once the integrator
  // takes over the budget through `setRejoinAttemptLimit`.
  private rejoinLongWindowRateLimiter?: SlidingWindowRateLimiter =
    new SlidingWindowRateLimiter(20, 30 * 60 * 1000);
  // In-flight `doJoin` runs, awaited before acting on peer connections that
  // gathered no local ICE candidates.
  private readonly joinTasks = new Set<Promise<void>>();
  private noLocalCandidatesCheckScheduled = false;
  // "Network doesn't support WebRTC" detector: counts peer-connection
  // failures where ICE never reached `connected`/`completed`.
  private maxIceFailuresWithoutConnect = 2;
  private iceFailuresWithoutConnect = 0;
  // Consecutive-negotiation-failure detector: stops the reconnect loop when
  // the SFU keeps failing to negotiate SDP for us.
  private maxConsecutiveNegotiationFailures = 3;
  private consecutiveNegotiationFailures = 0;
  // maintain the order of publishing tracks to restore them after a reconnection
  // it shouldn't contain duplicates
  private trackPublishOrder: TrackType[] = [];
  private joinResponseTimeout?: number;
  private rpcRequestTimeout?: number;
  private joinCallData?: JoinCallData;
  private allowOwnTracksLoopback = false;
  private hasJoinedOnce = false;
  private stopTracingPageLifecycle?: () => void;
  private deviceSettingsAppliedOnce = false;
  private callManagerStarted = false;
  private leaveGeneration = 0;
  private leavesInProgress = 0;
  private credentials?: Credentials;

  private initialized = false;
  private readonly acceptRejectConcurrencyTag = Symbol('acceptRejectTag');
  private readonly joinLeaveConcurrencyTag = Symbol('joinLeaveConcurrencyTag');

  /**
   * A list hooks/functions to invoke when the call is left.
   * A typical use case is to clean up some global event handlers.
   * @private
   */
  private readonly leaveCallHooks: Set<Function> = new Set();

  private streamClientEventHandlers = new Map<Function, () => void>();

  /**
   * A list of capabilities that the client supports and are enabled.
   */
  private clientCapabilities = new Set<ClientCapability>([
    ClientCapability.SUBSCRIBER_VIDEO_PAUSE,
  ]);

  /**
   * The in-flight per-call media engine. On web/React this resolves to a thin
   * globals-backed engine (no provider registered); React Native registers a
   * provider that owns a per-call native factory.
   * @internal
   */
  private mediaEnginePromise?: Promise<CallMediaEngine>;

  /**
   * Constructs a new `Call` instance.
   *
   * NOTE: Don't call the constructor directly, instead
   * Use the [`StreamVideoClient.call`](./StreamVideoClient.md/#call)
   * method to construct a `Call` instance.
   */
  constructor({
    type,
    id,
    streamClient,
    clientEventReporter,
    members,
    ownCapabilities,
    sortParticipantsBy,
    clientState,
    videoApi,
    ringing = false,
    watching = false,
  }: CallConstructor) {
    this.videoApi = videoApi;
    this.api = new CallApi(videoApi, type, id);
    this.type = type;
    this.id = id;
    this.cid = `${type}:${id}`;
    this.ringingSubject = new BehaviorSubject(ringing);
    this.watching = watching;
    this.streamClient = streamClient;
    this.clientEventReporter = clientEventReporter;
    this.clientState = clientState;
    this.logger = videoLoggerSystem.getLogger('Call');
    this.offlineState = new CallOfflineState({
      state: this.state,
      tracer: this.tracer,
      logger: this.logger,
      reachability: new NetworkReachabilityChecker({
        streamClient,
        getSfuClient: () => this.sfuClient,
        getSubscriber: () => this.subscriber,
      }),
      isLeaving: () => this.leavesInProgress > 0,
      hasPendingReconnect: () => hasPending(this.reconnectConcurrencyTag),
      connectionsIntact: () =>
        (this.publisher?.isIceIntact() ?? true) &&
        (this.subscriber?.isIceIntact() ?? true),
      discardSfuConnection: () =>
        this.sfuClient?.close(
          StreamSfuClient.DISPOSE_OLD_SOCKET,
          'Closing WS to reconnect after going online',
        ),
      reconnect: this.reconnectAfterOffline,
      setStatsPaused: (paused) =>
        paused ? this.sfuStatsReporter?.stop() : this.sfuStatsReporter?.start(),
    });

    const callTypeConfig = CallTypes.get(type);
    const participantSorter =
      sortParticipantsBy || callTypeConfig.options.sortParticipantsBy;
    if (participantSorter) {
      this.state.setSortParticipantsBy(participantSorter);
    }

    this.state.setMembers(members || []);
    this.state.setOwnCapabilities(ownCapabilities || []);
    this.state.setCallingState(
      ringing ? CallingState.RINGING : CallingState.IDLE,
    );

    const preferences = normalize(streamClient.options.devicePersistence);
    this.camera = new CameraManager(this, preferences);
    this.microphone = new MicrophoneManager(this, preferences);
    this.speaker = new SpeakerManager(this, preferences);
    this.screenShare = new ScreenShareManager(this);
    this.trackSubscriptionManager = new TrackSubscriptionManager(
      this.state,
      this.tracer,
    );
    this.blockedAudioTracker = new BlockedAudioTracker(this.tracer);
    this.leaveCallHooks.add(this.blockedAudioTracker.reset);

    if (typeof document !== 'undefined') {
      this.audioBindingsWatchdog = new AudioBindingsWatchdog(
        this.state,
        this.tracer,
      );
      this.viewportTracker = new ViewportTracker(this.state);
      this.dynascaleManager = new DynascaleManager(
        this.state,
        this.speaker,
        this.tracer,
        this.trackSubscriptionManager,
        this.blockedAudioTracker,
      );
    }
  }

  /**
   * Sets up the call instance.
   *
   * @internal an internal method and should not be used outside the SDK.
   */
  setup = async () => {
    await withoutConcurrency(this.joinLeaveConcurrencyTag, async () => {
      if (this.initialized) return;

      this.leaveCallHooks.add(
        this.on('all', (event) => {
          // update state with the latest event data
          this.state.updateFromEvent(event);
        }),
      );

      this.leaveCallHooks.add(
        this.on('changePublishOptions', (event) => {
          this.currentPublishOptions = event.publishOptions;
        }),
      );

      this.leaveCallHooks.add(registerEventHandlers(this, this.dispatcher));
      this.registerEffects();
      this.registerReconnectHandlers();

      // Set up the device managers and the audio bindings watchdog again.
      // Although this is already done in their constructors, they'll need to
      // be re-set up in the cases where a call instance is recycled
      // (join -> leave -> join).
      this.camera.setup();
      this.microphone.setup();
      this.screenShare.setup();
      this.speaker.setup();
      this.audioBindingsWatchdog?.setup();

      if (this.state.callingState === CallingState.LEFT) {
        this.state.setCallingState(CallingState.IDLE);
      }

      this.initialized = true;
    });
  };

  private registerEffects = () => {
    this.leaveCallHooks.add(
      // handles updating the permissions context when the settings change.
      createSubscription(this.state.settings$, (settings) => {
        if (!settings) return;
        this.permissionsContext.setCallSettings(settings);
      }),
    );

    this.leaveCallHooks.add(
      // handle the case when the user permissions are modified.
      createSafeAsyncSubscription(
        this.state.ownCapabilities$,
        this.handleOwnCapabilitiesUpdated,
      ),
    );

    this.leaveCallHooks.add(
      // handles the case when the user is blocked by the call owner.
      createSubscription(this.state.blockedUserIds$, async (blockedUserIds) => {
        if (!blockedUserIds || blockedUserIds.length === 0) return;
        const currentUserId = this.currentUserId;
        if (currentUserId && blockedUserIds.includes(currentUserId)) {
          this.logger.info('Leaving call because of being blocked');
          globalThis.streamRNVideoSDK?.callingX?.endCall(this, 'restricted');
          await this.leave({ message: 'user blocked' }).catch((err) => {
            this.logger.error('Error leaving call after being blocked', err);
          });
        }
      }),
    );

    if (this.ringing) {
      // if the call is ringing, we need to register the ringing call effects
      this.handleRingingCall();
    } else {
      // if the call is not ringing, we need to register the ringing call subscriptions
      // to handle the case when the call gets ringing flag after creation event
      this.leaveCallHooks.add(
        // "ringing" mode effects and event handlers
        createSubscription(this.ringingSubject, (isRinging) => {
          if (!isRinging) return;
          this.handleRingingCall();
        }),
      );
    }

    this.leaveCallHooks.add(
      // cancel auto-drop when call is accepted or rejected
      createSubscription(this.state.session$, (session) => {
        if (!this.ringing) return;

        const { settledByMe, leaveReason } = resolveOwnRingOutcome({
          session,
          currentUserId: this.currentUserId,
          callingState: this.state.callingState,
        });
        if (settledByMe) this.cancelAutoDrop();
        if (!leaveReason || this.leavesInProgress > 0) return;

        globalThis.streamRNVideoSDK?.callingX?.endCall(this, leaveReason);
        this.leave().catch(() => {
          this.logger.error(
            'Could not leave a call that was accepted or rejected elsewhere',
          );
        });
      }),
    );
  };

  private handleRingingCall = () => {
    const callSession = this.state.session;
    const receiver_id = this.clientState.connectedUser?.id;
    const ended_at = callSession?.ended_at;
    const created_by_id = this.state.createdBy?.id;

    if (this.currentUserId && created_by_id === this.currentUserId) {
      globalThis.streamRNVideoSDK?.callingX?.registerOutgoingCall(this);
    }
    const rejected_by = callSession?.rejected_by;
    const accepted_by = callSession?.accepted_by;
    let leaveCallIdle = false;
    if (ended_at) {
      // call was ended before it was accepted or rejected so we should leave it to idle
      leaveCallIdle = true;
    } else if (created_by_id && rejected_by) {
      if (rejected_by[created_by_id]) {
        // call was cancelled by the caller
        leaveCallIdle = true;
      }
    } else if (receiver_id && rejected_by) {
      if (rejected_by[receiver_id]) {
        // call was rejected by the receiver in some other device
        leaveCallIdle = true;
      }
    } else if (receiver_id && accepted_by) {
      if (accepted_by[receiver_id]) {
        // call was accepted by the receiver in some other device
        leaveCallIdle = true;
      }
    }
    if (leaveCallIdle) {
      if (this.state.callingState !== CallingState.IDLE) {
        this.state.setCallingState(CallingState.IDLE);
      }
    } else {
      if (this.state.callingState === CallingState.IDLE) {
        this.state.setCallingState(CallingState.RINGING);
      }
      this.scheduleAutoDrop();
      this.scheduleRingStatePolling();
      this.leaveCallHooks.add(registerRingingCallEventHandlers(this));
    }
  };

  private handleOwnCapabilitiesUpdated = async (
    ownCapabilities: OwnCapability[],
  ) => {
    // update the permission context.
    this.permissionsContext.setPermissions(ownCapabilities);

    if (!this.publisher) return;

    // check if the user still has publishing permissions and stop publishing if not.
    const permissionToTrackType = {
      [OwnCapability.SEND_AUDIO]: TrackType.AUDIO,
      [OwnCapability.SEND_VIDEO]: TrackType.VIDEO,
      [OwnCapability.SCREENSHARE]: TrackType.SCREEN_SHARE,
    };
    for (const [permission, trackType] of Object.entries(
      permissionToTrackType,
    )) {
      const hasPermission = this.permissionsContext.hasPermission(
        permission as OwnCapability,
      );
      if (hasPermission) continue;
      try {
        switch (trackType) {
          case TrackType.AUDIO:
            if (this.microphone.enabled) await this.microphone.disable();
            break;
          case TrackType.VIDEO:
            if (this.camera.enabled) await this.camera.disable();
            break;
          case TrackType.SCREEN_SHARE:
            if (this.screenShare.enabled) await this.screenShare.disable();
            break;
        }
      } catch (err) {
        this.logger.error(
          `Can't disable mic/camera/screenshare after revoked permissions`,
          err,
        );
      }
    }
  };

  /**
   * You can subscribe to WebSocket events provided by the API. To remove a subscription, call the `off` method.
   * Please note that subscribing to WebSocket events is an advanced use-case.
   * For most use-cases, it should be enough to watch for state changes.
   *
   * @param eventName the event name.
   * @param fn the event handler.
   */
  on = <E extends keyof AllCallEvents>(
    eventName: E,
    fn: CallEventListener<E>,
  ) => {
    if (isSfuEvent(eventName)) {
      return this.dispatcher.on(eventName, '*', fn);
    }

    const offHandler = this.streamClient.on(eventName, (e) => {
      const event = e as StreamCallEvent;
      if (event.call_cid && event.call_cid === this.cid) {
        fn(event as AllCallEvents[E]);
      }
    });

    // keep the 'off' reference returned by the stream client
    this.streamClientEventHandlers.set(fn, offHandler);
    return () => {
      this.off(eventName, fn);
    };
  };

  /**
   * Remove subscription for WebSocket events that were created by the `on` method.
   *
   * @param eventName the event name.
   * @param fn the event handler.
   */
  off = <E extends keyof AllCallEvents>(
    eventName: E,
    fn: CallEventListener<E>,
  ) => {
    if (isSfuEvent(eventName)) {
      return this.dispatcher.off(eventName, '*', fn);
    }

    // unsubscribe from the stream client event by using the 'off' reference
    const registeredOffHandler = this.streamClientEventHandlers.get(fn);
    if (registeredOffHandler) {
      registeredOffHandler();
    }
  };

  /**
   * Leave the call and stop the media streams that were published by the call.
   */
  leave = async ({ reject, reason, message }: CallLeaveOptions = {}) => {
    if (this.state.callingState === CallingState.LEFT) {
      throw new Error('Cannot leave call that has already been left.');
    }

    // before the first await: the calling state stays RINGING well into the
    // teardown, so pause both watchdogs before they can race this leave. They
    // keep their deadlines, so a failed leave resumes them without handing the
    // ring another window.
    this.ringTimeout?.pause();
    this.ringStatePoller?.pause();

    // also before the first await: `reconnect()` reads it to tell a teardown
    // in progress from a live call, as the calling state stays JOINED until
    // the very end.
    this.leavesInProgress += 1;
    await withoutConcurrency(this.joinLeaveConcurrencyTag, async () => {
      const callingState = this.state.callingState;

      if (callingState === CallingState.LEFT) {
        return;
      }

      this.leaveGeneration += 1;

      if (callingState === CallingState.JOINING) {
        const waitUntilCallJoined = () => {
          return new Promise<void>((resolve) => {
            this.state.callingState$
              .pipe(takeWhile((state) => state !== CallingState.JOINED, true))
              .subscribe(() => resolve());
          });
        };
        await waitUntilCallJoined();
      }

      if (callingState === CallingState.RINGING && reject !== false) {
        if (reject) {
          const reasonToEndCallReason = {
            timeout: 'missed',
            cancel: 'canceled',
            busy: 'busy',
            decline: 'rejected',
          } as const;
          const rejectReason = reason ?? 'decline';
          const endCallReason =
            reasonToEndCallReason[
              rejectReason as keyof typeof reasonToEndCallReason
            ] ?? 'rejected';
          await this.reject({ reason: rejectReason });
          globalThis.streamRNVideoSDK?.callingX?.endCall(this, endCallReason);
        } else {
          // if reject was undefined, we still have to cancel the call automatically
          // when I am the creator and everyone else left the call
          const hasOtherParticipants = this.state.remoteParticipants.length > 0;
          if (this.isCreatedByMe && !hasOtherParticipants) {
            await this.reject({ reason: 'cancel' });
            globalThis.streamRNVideoSDK?.callingX?.endCall(this, 'canceled');
          }
        }
      }
      globalThis.streamRNVideoSDK?.callingX?.endCall(
        this,
        reason === 'ended' ? 'remote' : undefined,
      );

      this.statsReporter?.stop();
      this.statsReporter = undefined;
      this.stopInboundAudioStallDetector?.();
      this.stopInboundAudioStallDetector = undefined;
      this.stopTracingPageLifecycle?.();
      this.stopTracingPageLifecycle = undefined;
      // release a reconnect waiting for the network; it sees the leave and stops
      this.offlineState.dispose();

      const leaveReason = message ?? reason ?? 'user is leaving the call';
      this.tracer.trace('call.leaveReason', leaveReason);
      // await the final sample so it's captured from the still-live peer
      // connections (disposed below); the send itself stays best-effort.
      await this.sfuStatsReporter?.flush();
      this.sfuStatsReporter?.stop();
      this.sfuStatsReporter = undefined;
      this.lastStatsOptions = undefined;

      await this.subscriber?.dispose();
      this.clientEventReporter.abort(this.cid, {
        code: 'CLIENT_ABORTED',
        reason: leaveReason,
      });

      this.subscriber = undefined;

      await this.publisher?.dispose();
      this.publisher = undefined;

      this.clientEventReporter.unregisterCall(this.cid);

      await this.sfuClient?.leaveAndClose(leaveReason);
      this.sfuClient = undefined;
      this.trackSubscriptionManager.setSfuClient(undefined);
      this.trackSubscriptionManager.dispose();
      this.audioBindingsWatchdog?.dispose();
      await this.dynascaleManager?.dispose();

      this.state.setCallingState(CallingState.LEFT);
      // `ringingSubject` is cleared further down, so this still reads true for
      // a call that was ringing.
      if (this.ringing) {
        globalThis.streamRNVideoSDK?.ringingCallLifecycle?.onLeave(this);
      }
      this.state.setParticipants([]);
      this.state.dispose();

      // Reset reconnect-related accumulators so a future `call.join()` on the
      // same instance starts with a fresh budget. The `Call` may be reused
      // (see `Call.test.ts` "can reuse call instance") so this is required.
      // Strategy/reason/attempts must also be cleared: when `leave()` is
      // reached via `giveUpAndLeave()` the success-path reset at the end of
      // `joinFlow` never runs, leaving stale values that would make the next
      // fresh `join()` send a stale `ReconnectDetails` to the SFU.
      this.rejoinRateLimiter.reset();
      this.rejoinLongWindowRateLimiter?.reset();
      this.iceFailuresWithoutConnect = 0;
      this.consecutiveNegotiationFailures = 0;
      this.reconnectStrategy = WebsocketReconnectStrategy.UNSPECIFIED;
      this.reconnectReason = '';
      this.reconnectAttempts = 0;

      // Call all leave call hooks, e.g. to clean up global event handlers
      this.leaveCallHooks.forEach((hook) => hook());
      this.initialized = false;
      this.hasJoinedOnce = false;
      this.allowOwnTracksLoopback = false;
      this.unifiedSessionId = undefined;
      this.ringingSubject.next(false);
      this.cancelAutoDrop();
      this.cancelRingStatePolling();
      this.clientState.unregisterCall(this);

      globalThis.streamRNVideoSDK?.callManager.stop({
        isRingingTypeCall: this.ringing,
        shouldStopCallManager: this.callManagerStarted,
      });

      this.camera.dispose();
      this.microphone.dispose();
      this.screenShare.dispose();
      this.speaker.dispose();
      this.deviceSettingsAppliedOnce = false;
      this.callManagerStarted = false;

      const stopOnLeavePromises: Promise<void>[] = [];
      if (this.camera.stopOnLeave) {
        stopOnLeavePromises.push(this.camera.disable(true));
      }
      if (this.microphone.stopOnLeave) {
        stopOnLeavePromises.push(this.microphone.disable(true));
      }
      if (this.screenShare.stopOnLeave) {
        stopOnLeavePromises.push(this.screenShare.disable(true));
      }
      await Promise.all(stopOnLeavePromises);

      // Dispose the per-call media engine last — after peer connections and
      // local tracks are gone — so the backing factory tears down with no
      // owned PCs/tracks. A fresh `join()` builds a new engine.
      if (this.mediaEnginePromise) {
        const enginePromise = this.mediaEnginePromise;
        this.mediaEnginePromise = undefined;
        this.logger.debug('Disposing per-call media factory');
        await enginePromise
          .then((engine) => engine.dispose())
          .catch((err) => {
            this.logger.warn('Failed to dispose media engine', err);
          });
      }
    })
      .catch((err) => {
        if (
          // only this leave (still counted until `finally`): a leave queued
          // behind it owns the watchdogs now, a queued `setup()` doesn't
          this.leavesInProgress === 1 &&
          this.state.callingState === CallingState.RINGING
        ) {
          // resume, never re-arm: a fresh watchdog would restart the ring
          // window this leave was already most of the way through
          this.ringTimeout?.start();
          this.ringStatePoller?.resume();
        }
        throw err;
      })
      .finally(() => {
        this.leavesInProgress -= 1;
      });
  };

  /**
   * A flag indicating whether the call is "ringing" type of call.
   */
  get ringing() {
    return getCurrentValue(this.ringingSubject);
  }

  /**
   * Retrieves the current user ID.
   */
  get currentUserId() {
    return this.clientState.connectedUser?.id;
  }

  /**
   * A flag indicating whether self-subscription is enabled for the call.
   */
  get isOwnTracksLoopbackAllowed() {
    return this.allowOwnTracksLoopback;
  }

  /**
   * The largest video publish dimension across the current publish options.
   *
   * @internal
   */
  getMaxVideoPublishDimension = (): VideoDimension | undefined => {
    if (!this.currentPublishOptions) return undefined;
    let maxDimension: VideoDimension | undefined;
    let maxArea = 0;
    for (const opt of this.currentPublishOptions) {
      if (opt.trackType !== TrackType.VIDEO) continue;

      const dim = opt.videoDimension;
      if (!dim || !dim.width || !dim.height) continue;

      const area = dim.width * dim.height;
      if (area > maxArea) {
        maxDimension = dim;
        maxArea = area;
      }
    }
    return maxDimension;
  };

  /**
   * A flag indicating whether the call was created by the current user.
   */
  get isCreatedByMe() {
    return (
      this.currentUserId && this.state.createdBy?.id === this.currentUserId
    );
  }

  /**
   * Update from the call response from the "call.ring" event
   * @internal
   */
  updateFromRingingEvent = async (event: CallRingEvent) => {
    await this.setup();
    // call.ring event excludes the call creator in the members list
    // as the creator does not get the ring event
    // so update the member list accordingly
    const { created_by, settings } = event.call;
    const creator = this.state.members.find((m) => m.user.id === created_by.id);
    if (!creator) {
      this.state.setMembers(event.members);
    } else {
      this.state.setMembers([creator, ...event.members]);
    }
    // update the call state with the latest event data
    this.state.updateFromCallResponse(event.call);
    this.watching = true;
    this.ringingSubject.next(true);
    // we remove the instance from the calls list to enable the following filter in useCalls hook
    // const calls = useCalls().filter((c) => c.ringing);
    const calls = this.clientState.calls.filter((c) => c.cid !== this.cid);
    this.clientState.setCalls([this, ...calls]);
    await this.applyDeviceConfig(settings, { publish: false });
  };

  /**
   * Applies a backend-provided call state snapshot
   * (call, members, own capabilities) to this instance.
   *
   * @internal an internal method and should not be used outside the SDK.
   */
  updateFromCallStateResponse = (response: CallStateResponseFields) => {
    this.state.updateFromCallResponse(response.call);
    this.state.setMembers(response.members);
    this.state.setOwnCapabilities(response.own_capabilities);
  };

  /**
   * Loads the information about the call.
   *
   * @param params.ring if set to true, a `call.ring` event will be sent to the call members.
   * @param params.notify if set to true, a `call.notification` event will be sent to the call members.
   * @param params.members_limit the total number of members to return as part of the response.
   * @param params.video if set to true, in a ringing scenario, mobile SDKs will show "incoming video call", audio only otherwise.
   */
  get = async (params?: {
    members_limit?: number;
    ring?: boolean;
    notify?: boolean;
    video?: boolean;
  }): Promise<StreamResponse<GetCallResponse>> => {
    const getLeaveGeneration = this.leaveGeneration;
    await this.setup();

    const response = await this.api.get(params);
    if (this.leaveGeneration !== getLeaveGeneration) return response;

    this.updateFromCallStateResponse(response);

    if (params?.ring) {
      this.ringingSubject.next(true);
    }

    if (this.streamClient._hasConnectionID()) {
      this.watching = true;
      this.clientState.registerOrUpdateCall(this);
    }
    await this.applyDeviceConfig(response.call.settings, { publish: false });

    return response;
  };

  /**
   * Loads the information about the call and creates it if it doesn't exist.
   *
   * @param data the data to create the call with.
   */
  getOrCreate = async (data?: GetOrCreateCallRequest) => {
    await this.setup();

    const response = await this.api.getOrCreate(data);

    this.updateFromCallStateResponse(response);

    if (data?.ring) {
      this.ringingSubject.next(true);
    }

    if (this.streamClient._hasConnectionID()) {
      this.watching = true;
      this.clientState.registerOrUpdateCall(this);
    }

    await this.applyDeviceConfig(response.call.settings, { publish: false });

    return response;
  };

  /**
   * Creates a call
   *
   * @param data the data to create the call with.
   */
  create = async (data?: GetOrCreateCallRequest) => {
    return this.getOrCreate(data);
  };

  /**
   * Returns who accepted, rejected or missed the ring for a call session.
   * Safe to poll: it performs no writes and emits no events.
   */
  getRingState = async ({
    call_session_id = this.state.session?.id,
  }: {
    /**
     * The call session to read, the current one by default. Pass it explicitly
     * to read a session that has already ended, as ending a call clears its
     * current session.
     */
    call_session_id?: string;
  } = {}) => {
    if (!call_session_id) {
      throw new Error('Cannot read the ring state: the call has no session');
    }
    return this.api.getCallRingState({ call_session_id });
  };

  /**
   * A shortcut for {@link Call.get} with `notify` parameter set to `true`.
   * Will send a `call.notification` event to the call members.
   */
  notify = async (): Promise<StreamResponse<GetCallResponse>> => {
    return await this.get({ notify: true });
  };

  /**
   * Marks the incoming call as accepted.
   *
   * This method should be used only for "ringing" call flows.
   * {@link Call.join} invokes this method automatically for you when joining a call.
   * Unless you are implementing a custom "ringing" flow, you should not use this method.
   */
  accept = async () => {
    return withoutConcurrency(this.acceptRejectConcurrencyTag, () => {
      this.tracer.trace('call.accept', '');
      return this.api.accept();
    });
  };

  /**
   * Marks the incoming call as rejected.
   *
   * This method should be used only for "ringing" call flows.
   * {@link Call.leave} invokes this method automatically for you when you leave or reject this call.
   * Unless you are implementing a custom "ringing" flow, you should not use this method.
   */
  reject = async ({
    reason = 'decline',
  }: {
    /** The reason for rejecting the call, `'decline'` by default. */
    reason?: RejectReason;
  } = {}) => {
    return withoutConcurrency(this.acceptRejectConcurrencyTag, () => {
      this.tracer.trace('call.reject', reason);
      return this.api.reject({ reason });
    });
  };

  /**
   * Will start to watch for call related WebSocket events and initiate a call session with the server.
   *
   * One instance is one call flow: discard it after leaving, cancelling, or a
   * failed join, and create a fresh one for a later flow. Reconnection inside a
   * live call is handled here and needs no new instance.
   *
   * @returns a promise which resolves once the call join-flow has finished.
   */
  join = singleFlight(
    async ({
      maxJoinRetries = 3,
      joinResponseTimeout,
      rpcRequestTimeout,
      allowOwnTracksLoopback = false,
      joinSource,
      ...data
    }: JoinCallData & {
      maxJoinRetries?: number;
      joinResponseTimeout?: number;
      rpcRequestTimeout?: number;
      allowOwnTracksLoopback?: boolean;
      joinSource?: JoinSource;
    } = {}): Promise<void> => {
      const callingState = this.state.callingState;

      // Ahead of the failure boundary below on purpose: a duplicate join on a
      // live call is refused without tearing that call down.
      if ([CallingState.JOINED, CallingState.JOINING].includes(callingState)) {
        throw new Error(`Illegal State: call.join() shall be called only once`);
      }

      // snapshot before the first await: a leave() landing at any point from
      // here on supersedes this join, and the retry loop must bail out instead
      // of resurrecting a call that leave() already tore down.
      const joinLeaveGeneration = this.leaveGeneration;
      const supersededByLeave = () =>
        this.leaveGeneration !== joinLeaveGeneration;

      this.dynascaleManager?.keepAudioPlaybackUnlocked();

      // we need this to be set before the callingx.joinCall() is
      // called to avoid registering the test call in the CallKit/Telecom
      this.allowOwnTracksLoopback = allowOwnTracksLoopback;

      if (data?.ring) {
        this.ringingSubject.next(true);
      }

      // A ringing call is joined by the SDK rather than by app code, so React
      // Native prepares it from in here - there is no earlier point at which the
      // app holds the call. Read after `data.ring` above, which is what makes an
      // outgoing call ringing in the first place.
      const ringingLifecycle = this.ringing
        ? globalThis.streamRNVideoSDK?.ringingCallLifecycle
        : undefined;
      const callingX = globalThis.streamRNVideoSDK?.callingX;
      try {
        if (ringingLifecycle) {
          await ringingLifecycle.beforeJoin(this);
          if (supersededByLeave()) {
            this.logger.debug('Join superseded by leave; not joining');
            return;
          }
        }

        if (callingX) {
          // for Android/iOS, we need to start the call in the callingx library as soon as possible
          await callingX.joinCall(
            this,
            this.clientState.calls,
            supersededByLeave,
          );
          if (supersededByLeave()) {
            this.logger.debug('Join superseded by leave; not setting up');
            return;
          }
        }

        await this.setup();
        if (supersededByLeave()) {
          this.logger.debug('Join superseded by leave; not registering');
          return;
        }

        this.clientEventReporter.registerCall(this.cid, {
          callType: this.type,
          callId: this.id,
          getCallSessionId: () => this.state.session?.id ?? '',
          getSfuId: () => this.credentials?.server.edge_name ?? '',
        });

        this.joinResponseTimeout = joinResponseTimeout;
        this.rpcRequestTimeout = rpcRequestTimeout;
        // we will count the number of join failures per SFU.
        // once the number of failures reaches 2, we will piggyback on the `migrating_from`
        // field to force the coordinator to provide us another SFU
        const sfuJoinFailures = new Map<string, number>();
        const joinData: JoinCallData = data;
        maxJoinRetries = Math.max(maxJoinRetries, 1);
        await this.clientEventReporter.withJoinLifecycle(
          this.cid,
          { joinReason: 'first-attempt', joinSource },
          async () => {
            for (let attempt = 0; attempt < maxJoinRetries; attempt++) {
              // Also covers a leave that lands during the backoff below.
              if (supersededByLeave()) {
                this.logger.debug('Join superseded by leave; not attempting');
                return;
              }
              try {
                this.logger.trace(`Joining call (${attempt})`, this.cid);
                await this.doJoin(data);
                delete joinData.migrating_from;
                delete joinData.migrating_from_list;
                return;
              } catch (err) {
                if (supersededByLeave()) {
                  this.logger.debug('Join superseded by leave; not retrying');
                  return;
                }
                this.logger.warn(`Failed to join call (${attempt})`, this.cid);
                if (
                  (err instanceof ErrorFromResponse && err.unrecoverable) ||
                  (err instanceof SfuJoinError && err.unrecoverable)
                ) {
                  throw err;
                }

                const switchSfu =
                  err instanceof SfuJoinError &&
                  SfuJoinError.isJoinErrorCode(err.errorEvent);

                const sfuId = this.credentials?.server.edge_name;
                if (sfuId) {
                  const failures = (sfuJoinFailures.get(sfuId) || 0) + 1;
                  sfuJoinFailures.set(sfuId, failures);
                  if (switchSfu || failures >= 2) {
                    joinData.migrating_from = sfuId;
                    joinData.migrating_from_list = Array.from(
                      sfuJoinFailures.keys(),
                    );
                    if (attempt < maxJoinRetries - 1) {
                      this.clientEventReporter.startCorrelation(
                        this.cid,
                        'first-attempt',
                      );
                    }
                  }
                }

                if (attempt === maxJoinRetries - 1) {
                  throw err;
                }
              }
              await sleep(retryInterval(attempt));
            }
          },
        );
      } catch (error) {
        this.dynascaleManager?.releaseAudioPlaybackUnlock();
        callingX?.endCall(this, 'error');
        // Ends the failed ringing flow and releases what its setup installed.
        // Never rejects, so `error` is what the caller sees.
        await ringingLifecycle?.onJoinFailed(this);
        throw error;
      }
    },
  );

  /**
   * Will make a single attempt to watch for call related WebSocket events
   * and initiate a call session with the server.
   *
   * @returns a promise which resolves once the call join-flow has finished.
   */
  private doJoin = async (data?: JoinCallData): Promise<void> => {
    const task = this.performJoin(data);
    this.joinTasks.add(task);
    try {
      await task;
    } finally {
      this.joinTasks.delete(task);
    }
  };

  private performJoin = async (data?: JoinCallData): Promise<void> => {
    // only a fast reconnect arms this, never carry one over from a previous join
    this.fastReconnectOffer = undefined;
    const connectStartTime = Date.now();
    const callingState = this.state.callingState;
    const joinLeaveGeneration = this.leaveGeneration;
    const supersededByLeave = () =>
      this.leaveGeneration !== joinLeaveGeneration;

    this.joinCallData = data;

    this.logger.debug('Starting join flow');
    this.state.setCallingState(CallingState.JOINING);

    // Ensure the per-call media engine exists before any peer connection
    // (codec probe, subscriber, publisher) or capture happens, so the WebRTC
    // globals resolve to the call's factory. Idempotent across
    // reconnect/migration attempts.
    await this.ensureMediaFactory();
    if (supersededByLeave()) {
      this.logger.debug('Join superseded by leave; not wiring media');
      return;
    }

    const performingMigration =
      this.reconnectStrategy === WebsocketReconnectStrategy.MIGRATE;
    const performingRejoin =
      this.reconnectStrategy === WebsocketReconnectStrategy.REJOIN;
    const performingFastReconnect =
      this.reconnectStrategy === WebsocketReconnectStrategy.FAST;

    let statsOptions = this.lastStatsOptions;
    if (
      !this.credentials ||
      !statsOptions ||
      performingRejoin ||
      performingMigration ||
      data?.migrating_from
    ) {
      try {
        const joinResponse = await this.clientEventReporter.track(
          this.cid,
          'CoordinatorJoin',
          () => this.doJoinRequest(data),
        );
        if (!joinResponse || supersededByLeave()) {
          this.logger.debug(
            'Join superseded by leave; not creating SFU client',
          );
          return;
        }
        this.credentials = joinResponse.credentials;
        statsOptions = joinResponse.stats_options;
        this.lastStatsOptions = statsOptions;
      } catch (error) {
        if (supersededByLeave()) return;
        // prevent triggering reconnect flow if the state is OFFLINE
        const avoidRestoreState =
          this.state.callingState === CallingState.OFFLINE;

        if (!avoidRestoreState) {
          // restore the previous call state if the join-flow fails
          this.state.setCallingState(callingState);
        }
        throw error;
      }
    }

    const previousSfuClient = this.sfuClient;
    const previousSessionId = previousSfuClient?.sessionId;
    const isWsHealthy = !!previousSfuClient?.isHealthy;
    const sfuClient =
      performingRejoin || performingMigration || !isWsHealthy
        ? new StreamSfuClient({
            tag: String(this.sfuClientTag++),
            cid: this.cid,
            dispatcher: this.dispatcher,
            credentials: this.credentials,
            streamClient: this.streamClient,
            enableTracing: statsOptions.enable_rtc_stats,
            joinResponseTimeout: this.joinResponseTimeout,
            rpcRequestTimeout: this.rpcRequestTimeout,
            // a new session_id is necessary for the REJOIN strategy.
            // we use the previous session_id if available
            sessionId: performingRejoin ? undefined : previousSessionId,
            onSignalClose: (reason) =>
              this.handleSfuSignalClose(sfuClient, reason),
          })
        : previousSfuClient;
    this.sfuClient = sfuClient;
    this.unifiedSessionId ??= sfuClient.sessionId;
    this.trackSubscriptionManager.setSfuClient(sfuClient);

    const clientDetails = await getClientDetails();
    // we don't need to send JoinRequest if we are re-using an existing healthy SFU client
    if (previousSfuClient !== sfuClient) {
      // prepare a generic SDP and send it to the SFU.
      // these are throw-away SDPs that the SFU will use to determine
      // the capabilities of the client (codec support, etc.)
      const { dangerouslyForceCodec, fmtpLine, subscriberFmtpLine } =
        this.clientPublishOptions || {};
      // skip if a leave superseded this join so codec detection doesn't resolve to a default factory.
      if (supersededByLeave()) {
        this.logger.debug('Join superseded by leave; skipping codec detection');
        return;
      }
      const [subscriberSdp, publisherSdp] = await Promise.all([
        getGenericSdp('recvonly', dangerouslyForceCodec, subscriberFmtpLine),
        getGenericSdp('sendonly', dangerouslyForceCodec, fmtpLine),
      ]);
      if (supersededByLeave()) {
        this.logger.debug('Join superseded by leave; not joining SFU');
        return;
      }
      const isReconnecting =
        this.reconnectStrategy !== WebsocketReconnectStrategy.UNSPECIFIED;
      const reconnectDetails = isReconnecting
        ? this.getReconnectDetails(
            previousSfuClient?.edgeName,
            previousSessionId,
          )
        : undefined;
      const preferredPublishOptions = !isReconnecting
        ? this.getPreferredPublishOptions()
        : this.currentPublishOptions || [];
      const preferredSubscribeOptions = !isReconnecting
        ? this.getPreferredSubscribeOptions()
        : [];

      const unifiedSessionId = this.unifiedSessionId;
      const capabilities = Array.from(this.clientCapabilities);
      try {
        const {
          callState,
          fastReconnectDeadlineSeconds,
          publishOptions,
          reconnected,
        } = await this.clientEventReporter.track(this.cid, 'WSJoin', () =>
          sfuClient.join({
            unifiedSessionId,
            subscriberSdp,
            publisherSdp,
            clientDetails,
            fastReconnect: performingFastReconnect,
            reconnectDetails,
            preferredPublishOptions,
            preferredSubscribeOptions,
            capabilities,
            source: ParticipantSource.WEBRTC_UNSPECIFIED,
          }),
        );
        if (supersededByLeave()) {
          this.logger.debug(
            'Join superseded by leave; not applying SFU response',
          );
          return;
        }

        if (performingFastReconnect && !reconnected) {
          // The SFU couldn't resume our session (e.g. it was cleaned up while
          // this client was frozen). It won't restart the subscriber, so the
          // call would stay silent while reporting JOINED: rejoin instead.
          throw new FastReconnectRejectedError(
            'SFU did not resume the session after a fast reconnect',
          );
        }

        this.currentPublishOptions = publishOptions;
        this.fastReconnectDeadlineSeconds = fastReconnectDeadlineSeconds;
        if (callState) {
          this.state.updateFromSfuCallState(
            callState,
            sfuClient.sessionId,
            reconnectDetails,
          );
        }
      } catch (error) {
        if (supersededByLeave()) {
          this.logger.debug('Join superseded by leave; ignoring SFU failure');
          return;
        }
        this.logger.warn('Join SFU request failed', error);
        sfuClient.close(
          StreamSfuClient.JOIN_FAILED,
          'Join request failed, connection considered unhealthy',
        );
        // restore the previous call state if the join-flow fails
        this.state.setCallingState(callingState);
        throw error;
      }
    }

    // If the user left while this join was in flight, bail before re-setting JOINED
    // or starting peer-connection setup below.
    if (supersededByLeave()) {
      this.logger.debug('Join superseded by leave; aborting join flow');
      return;
    }

    if (!performingMigration) {
      // in MIGRATION, `JOINED` state is set in `this.reconnectMigrate()`
      this.state.setCallingState(CallingState.JOINED);
    }
    this.hasJoinedOnce = true;
    this.stopTracingPageLifecycle ??= tracePageLifecycle({
      tracer: this.tracer,
      onHidden: () => {
        if (!this.lastStatsOptions?.enable_rtc_stats) return;
        this.sfuStatsReporter?.flush().catch((err) => {
          this.logger.warn('Failed to flush stats on page hide', err);
        });
      },
    });

    // when performing fast reconnect, or when we reuse the same SFU client,
    // (ws remained healthy), we just need to restore the ICE connection
    if (performingFastReconnect) {
      // the SFU automatically issues an ICE restart on the subscriber
      // we don't have to do it ourselves, but `reconnectFast` rejoins if it
      // never arrives. Armed before `restoreICE` moves the subscriber to the
      // new SFU client, as no offer from that client can reach it before.
      // The SFU only restarts the subscriber in response to a fast-reconnect
      // join: when the healthy client was reused, no join was sent and no
      // offer is coming, so don't wait for one.
      if (previousSfuClient !== sfuClient) {
        this.fastReconnectOffer = this.subscriber?.waitForOffer();
      }
      await this.restoreICE(sfuClient, { includeSubscriber: false });
    } else {
      const connectionConfig = toRtcConfiguration(this.credentials.ice_servers);
      await this.initPublisherAndSubscriber({
        sfuClient,
        connectionConfig,
        clientDetails,
        statsOptions,
        publishOptions: this.currentPublishOptions || [],
        closePreviousInstances: !performingMigration,
        unifiedSessionId: this.unifiedSessionId,
      });
    }
    if (supersededByLeave()) {
      this.logger.debug('Join superseded by leave; not completing join flow');
      return;
    }

    // make sure we only track connection timing if we are not calling this method as part of a reconnection flow
    if (!performingRejoin && !performingFastReconnect && !performingMigration) {
      this.sfuStatsReporter?.sendConnectionTime(
        (Date.now() - connectStartTime) / 1000,
      );
    }

    if (performingRejoin && isWsHealthy) {
      const strategy = WebsocketReconnectStrategy[this.reconnectStrategy];
      await previousSfuClient?.leaveAndClose(
        `Closing previous WS after reconnect with strategy: ${strategy}`,
      );
    } else if (!isWsHealthy) {
      previousSfuClient?.close(
        StreamSfuClient.DISPOSE_OLD_SOCKET,
        'Closing unhealthy WS after reconnect',
      );
    }

    // React Native: start the native call manager once per join, before the
    // first publish (fire-and-forget; on iOS the native engine gate makes the
    // order with the publish irrelevant). leave() stops it (callManagerStarted).
    if (!this.callManagerStarted && !supersededByLeave()) {
      globalThis.streamRNVideoSDK?.callManager.start({
        isRingingTypeCall: this.ringing,
        cid: this.cid,
        defaultDevice: getRNDefaultAudioDevice(this.state.settings),
      });
      this.callManagerStarted = true;
    }

    // device settings should be applied only once, we don't have to
    // re-apply them on later reconnections or server-side data fetches
    if (
      !this.deviceSettingsAppliedOnce &&
      this.state.settings &&
      !supersededByLeave()
    ) {
      await this.applyDeviceConfig(this.state.settings, { publish: true });
      this.deviceSettingsAppliedOnce = true;
    }

    // We shouldn't persist the `ring` and `notify` state after joining the call
    // as it's a one-time event and clashes with the potential reconnection attempts.
    // When reconnecting, if provided with `ring: true` or `notify: true`,
    // we will spam the other participants with push notifications and `call.ring` events.
    delete this.joinCallData?.ring;
    delete this.joinCallData?.notify;
    // reset the reconnect strategy to unspecified after a successful reconnection
    this.reconnectStrategy = WebsocketReconnectStrategy.UNSPECIFIED;
    this.reconnectReason = '';
    // A successful SFU join handshake resets the consecutive-negotiation
    // counter (negotiation just succeeded). It does NOT reset
    // `iceFailuresWithoutConnect` or the rolling `rejoinRateLimiter`;
    // those track WebRTC-level health and rejoin frequency, which are not
    // proven by the SFU handshake alone. ICE-failures-without-connect is
    // cleared via the `onIceConnected` callback when the peer connection
    // actually reaches `connected`/`completed` end-to-end. The rejoin
    // rolling window decays naturally as old timestamps age out.
    this.consecutiveNegotiationFailures = 0;

    this.logger.info(`Joined call ${this.cid}`);
  };

  /**
   * Prepares Reconnect Details object.
   * @internal
   */
  private getReconnectDetails = (
    migratingFromSfuId: string | undefined,
    previousSessionId: string | undefined,
  ): ReconnectDetails => {
    const strategy = this.reconnectStrategy;
    const performingRejoin = strategy === WebsocketReconnectStrategy.REJOIN;
    const announcedTracks =
      this.publisher?.getAnnouncedTracksForReconnect() || [];
    return {
      strategy,
      announcedTracks,
      subscriptions: this.trackSubscriptionManager.subscriptions,
      reconnectAttempt: this.reconnectAttempts,
      fromSfuId: migratingFromSfuId || '',
      previousSessionId: performingRejoin ? previousSessionId || '' : '',
      reason: this.reconnectReason,
    };
  };

  /**
   * Prepares the preferred codec for the call.
   * This is an experimental client feature and subject to change.
   * @internal
   */
  private getPreferredPublishOptions = (): PublishOption[] => {
    const { preferredCodec, fmtpLine, preferredBitrate, maxSimulcastLayers } =
      this.clientPublishOptions || {};
    if (!preferredCodec && !preferredBitrate && !maxSimulcastLayers) return [];

    const codec = preferredCodec
      ? Codec.create({ name: preferredCodec.split('/').pop(), fmtp: fmtpLine })
      : undefined;

    const preferredPublishOptions = [
      PublishOption.create({
        trackType: TrackType.VIDEO,
        codec,
        bitrate: preferredBitrate,
        maxSpatialLayers: maxSimulcastLayers,
      }),
    ];

    const screenShareSettings = this.screenShare.getSettings();
    if (screenShareSettings) {
      preferredPublishOptions.push(
        PublishOption.create({
          trackType: TrackType.SCREEN_SHARE,
          fps: screenShareSettings.maxFramerate,
          bitrate: screenShareSettings.maxBitrate,
        }),
      );
    }

    return preferredPublishOptions;
  };

  /**
   * Prepares the preferred options for subscribing to tracks.
   * This is an experimental client feature and subject to change.
   * @internal
   */
  private getPreferredSubscribeOptions = (): SubscribeOption[] => {
    const { subscriberCodec, subscriberFmtpLine } =
      this.clientPublishOptions || {};
    if (!subscriberCodec || !subscriberFmtpLine) return [];
    return [
      SubscribeOption.create({
        trackType: TrackType.VIDEO,
        codecs: [
          { name: subscriberCodec.split('/').pop(), fmtp: subscriberFmtpLine },
        ],
      }),
    ];
  };

  /**
   * Performs an ICE restart on both the Publisher and Subscriber Peer Connections.
   * Uses the provided SFU client to restore the ICE connection.
   *
   * This method can throw an error if the ICE restart fails.
   * This error should be handled by the reconnect loop,
   * and a new reconnection shall be attempted.
   *
   * @internal
   */
  private restoreICE = async (
    nextSfuClient: StreamSfuClient,
    opts: { includeSubscriber?: boolean; includePublisher?: boolean } = {},
  ) => {
    const { includeSubscriber = true, includePublisher = true } = opts;
    if (this.subscriber) {
      this.subscriber.setSfuClient(nextSfuClient);
      if (includeSubscriber) {
        await this.subscriber.restartIce();
      }
    }
    if (this.publisher) {
      this.publisher.setSfuClient(nextSfuClient);
      if (includePublisher && this.publisher.isPublishing()) {
        await this.publisher.restartIce();
      }
    }
  };

  /**
   * Initializes the Publisher and Subscriber Peer Connections.
   * @internal
   */
  private initPublisherAndSubscriber = async (opts: {
    sfuClient: StreamSfuClient;
    connectionConfig: RTCConfiguration;
    statsOptions: StatsOptions;
    clientDetails: ClientDetails;
    publishOptions: PublishOption[];
    closePreviousInstances: boolean;
    unifiedSessionId: string;
  }) => {
    const joinLeaveGeneration = this.leaveGeneration;
    const {
      sfuClient,
      connectionConfig,
      clientDetails,
      statsOptions,
      publishOptions,
      closePreviousInstances,
      unifiedSessionId,
    } = opts;
    const {
      enable_rtc_stats: enableTracing,
      reporting_interval_ms: reportingIntervalMs,
    } = statsOptions;
    const previousSubscriber = this.subscriber;
    const previousSfuStatsReporter = this.sfuStatsReporter;
    const basePeerConnectionOptions: BasePeerConnectionOpts = {
      sfuClient,
      dispatcher: this.dispatcher,
      state: this.state,
      connectionConfig,
      tag: sfuClient.tag,
      enableTracing,
      statsTimestampDriftThresholdMs: reportingIntervalMs / 2,
      clientPublishOptions: this.clientPublishOptions,
      e2ee: this.e2eeManager,
      onReconnectionNeeded: (kind, reason, peerType) => {
        this.reconnect(kind, reason).catch((err) => {
          const message = `[Reconnect] Error reconnecting, after a ${PeerType[peerType]} error: ${reason}`;
          this.logger.warn(message, err);
        });
      },
      onIceConnected: () => {
        // ICE has reached `connected`/`completed` end-to-end on at least
        // one peer connection, WebRTC is actually working, so the
        // "ICE never connected" failure budget can be cleared.
        this.iceFailuresWithoutConnect = 0;
      },
      onNoLocalCandidates: () => {
        this.checkNoLocalCandidates();
      },
      onPeerConnectionStateChange: (event) => {
        this.clientEventReporter.onPeerConnectionStateChange(this.cid, event);
      },
      onRemoteTrackUnmute: (trackType, trackId) => {
        const reportable =
          trackType === TrackType.AUDIO ||
          (isReactNative() && trackType === TrackType.VIDEO);

        if (!reportable) return;
        this.clientEventReporter.reportFirstFrame(this.cid, trackType, trackId);
      },
    };

    // The SFU sends the subscriber offer right after the join response, and
    // the dispatcher drops events nobody listens to. Create the subscriber
    // (which registers the offer listener) before awaiting anything.
    if (closePreviousInstances) {
      // Keep the old peer alive for stats, but prevent late tracks from
      // replacing and stopping streams received by the new subscriber.
      previousSubscriber?.detachEventHandlers();
      this.state.removeAllOrphanedTracks();
    }
    try {
      this.subscriber = new Subscriber(basePeerConnectionOptions);

      await previousSfuStatsReporter?.flush();
    } finally {
      previousSfuStatsReporter?.stop();
      if (this.sfuStatsReporter === previousSfuStatsReporter) {
        this.sfuStatsReporter = undefined;
      }
    }
    if (closePreviousInstances) await previousSubscriber?.dispose();
    if (this.leaveGeneration !== joinLeaveGeneration) return;

    // anonymous users can't publish anything hence, there is no need
    // to create Publisher Peer Connection for them
    const isAnonymous = this.streamClient.user?.type === 'anonymous';
    if (!isAnonymous) {
      if (closePreviousInstances && this.publisher) {
        await this.publisher.dispose();
        if (this.leaveGeneration !== joinLeaveGeneration) return;
      }
      this.publisher = new Publisher(
        basePeerConnectionOptions,
        publishOptions,
        {
          selfSubEnabled: this.allowOwnTracksLoopback,
        },
      );
    }

    this.statsReporter?.stop();
    if (this.statsReportingIntervalInMs > 0) {
      this.statsReporter = createStatsReporter({
        subscriber: this.subscriber,
        publisher: this.publisher,
        state: this.state,
        datacenter: sfuClient.edgeName,
        pollingIntervalInMs: this.statsReportingIntervalInMs,
        cameraState: this.camera.state,
      });
    }

    this.stopInboundAudioStallDetector?.();
    this.stopInboundAudioStallDetector =
      enableTracing && this.subscriber
        ? createInboundAudioStallDetector({
            state: this.state,
            subscriber: this.subscriber,
            tracer: this.tracer,
          })
        : undefined;

    this.tracer.setEnabled(enableTracing);
    if (statsOptions?.reporting_interval_ms > 0) {
      this.sfuStatsReporter = new SfuStatsReporter(sfuClient, {
        clientDetails,
        options: statsOptions,
        subscriber: this.subscriber,
        publisher: this.publisher,
        tracer: this.tracer,
        unifiedSessionId,
      });
      this.sfuStatsReporter.start();
    }
  };

  /**
   * Escalates to REJOIN with `ICE_NEVER_CONNECTED` when the current peer
   * connections never connected and one of them completed ICE gathering
   * without a single local candidate, which means WebRTC networking is
   * blocked on this device.
   *
   * `reconnect` ignores requests while a join or another reconnect is in
   * flight, so the check is postponed until those settle and then re-run
   * against the peer connections current at that time.
   */
  private checkNoLocalCandidates = () => {
    if (this.noLocalCandidatesCheckScheduled) return;
    if (this.joinTasks.size > 0 || hasPending(this.reconnectConcurrencyTag)) {
      this.noLocalCandidatesCheckScheduled = true;
      const leaveGeneration = this.leaveGeneration;
      this.waitForJoinsAndReconnects()
        .then(() => {
          this.noLocalCandidatesCheckScheduled = false;
          if (this.leaveGeneration !== leaveGeneration) return;
          this.checkNoLocalCandidates();
        })
        .catch((err) => {
          this.noLocalCandidatesCheckScheduled = false;
          this.logger.warn('[Reconnect] No-candidates check failed', err);
        });
      return;
    }

    if (this.state.callingState !== CallingState.JOINED) return;
    // without network, gathering legitimately yields no candidates
    if (isBrowserOffline()) return;
    const peerConnections = [this.publisher, this.subscriber];
    if (peerConnections.some((pc) => pc?.hasIceEverConnected())) return;
    if (!peerConnections.some((pc) => pc?.hasNoLocalCandidates())) return;

    this.reconnect(
      WebsocketReconnectStrategy.REJOIN,
      ReconnectReason.ICE_NEVER_CONNECTED,
    ).catch((err) => {
      this.logger.warn('[Reconnect] Error reconnecting', err);
    });
  };

  /**
   * Rejoins the call to rebuild the media stack, the last resort of the
   * React Native SDK when the iOS audio engine can't be restarted.
   * Skipped (returns `false`) when the call isn't JOINED, a reconnect is
   * already pending, or the rejoin rate limiters have no room: the
   * reconnect would then leave the call, which is worse than a dead mic.
   *
   * @internal
   * @returns `true` if the rejoin started.
   */
  rejoinForAudioEngineRecovery = (): boolean => {
    if (
      this.state.callingState !== CallingState.JOINED ||
      hasPending(this.reconnectConcurrencyTag) ||
      !this.rejoinRateLimiter.canRegister() ||
      this.rejoinLongWindowRateLimiter?.canRegister() === false
    ) {
      return false;
    }
    this.reconnect(
      WebsocketReconnectStrategy.REJOIN,
      'audio_engine_dead',
    ).catch((err) => {
      this.logger.warn('[Reconnect] Error rejoining for the audio engine', err);
    });
    return true;
  };

  /**
   * Resolves once no `doJoin` run and no reconnect is in flight.
   */
  private waitForJoinsAndReconnects = async () => {
    while (
      this.joinTasks.size > 0 ||
      hasPending(this.reconnectConcurrencyTag)
    ) {
      await Promise.allSettled(this.joinTasks);
      await settled(this.reconnectConcurrencyTag);
    }
  };

  /**
   * Retrieves credentials for joining the call.
   *
   * @internal
   * @param data the join call data.
   * @returns The coordinator response, or undefined if leave superseded the request before it was sent.
   */
  doJoinRequest = async (
    data?: JoinCallData,
  ): Promise<JoinCallResponse | undefined> => {
    const joinLeaveGeneration = this.leaveGeneration;
    const location = await this.streamClient.getLocationHint();
    if (this.leaveGeneration !== joinLeaveGeneration) {
      this.logger.debug(
        'Join superseded by leave; not sending coordinator request',
      );
      return;
    }
    const e2ee = !!this.e2eeManager;
    const joinResponse = await this.api.join({ ...data, location, e2ee });

    if (this.leaveGeneration !== joinLeaveGeneration) {
      this.logger.debug(
        'Join superseded by leave; not applying coordinator response',
      );
      return joinResponse;
    }

    this.state.updateFromCallResponse(joinResponse.call);
    this.state.setMembers(joinResponse.members);
    this.state.setOwnCapabilities(joinResponse.own_capabilities);

    if (data?.ring) {
      this.ringingSubject.next(true);
    }

    const isReconnecting =
      this.reconnectStrategy !== WebsocketReconnectStrategy.UNSPECIFIED;

    if (!isReconnecting && this.ringing && !this.isCreatedByMe) {
      // signals other users that I have accepted the incoming call.
      await this.accept();
      if (this.leaveGeneration !== joinLeaveGeneration) return joinResponse;
    }

    if (this.streamClient._hasConnectionID()) {
      this.watching = true;
      this.clientState.registerOrUpdateCall(this);
    }

    return joinResponse;
  };

  /**
   * Whether the per-call media engine currently exists. True from join until leave.
   *
   * @internal an internal getter and should not be used outside the SDK.
   */
  get hasMediaEngine(): boolean {
    return !!this.mediaEnginePromise;
  }

  /**
   * Captures camera and microphone through the given window's
   * `navigator.mediaDevices`, for example a Document Picture-in-Picture window
   * (Firefox blocks capture from a backgrounded tab). Shorthand for calling
   * `setCaptureWindow` on both `call.camera` and `call.microphone`.
   * Web only. Pass undefined to restore the default.
   */
  setCaptureWindow = (win: Window | undefined) => {
    this.camera.setCaptureWindow(win);
    this.microphone.setCaptureWindow(win);
  };

  /**
   * Ensures a {@link CallMediaEngine} exists for this call's media session and
   * returns it. Idempotent: the engine is created once via the registered
   * provider (see `setCallMediaEngineProvider`) and cached until `leave()`
   * disposes it. Concurrent callers (e.g. camera + microphone enabling in
   * parallel) share the same engine because the in-flight creation promise is
   * cached, never the unresolved result.
   *
   * @internal
   */
  ensureMediaFactory = async (): Promise<CallMediaEngine> => {
    if (!this.mediaEnginePromise) {
      const provider = getCallMediaEngineProvider();

      this.logger.debug(`Requesting per-call media factory creation`);
      this.mediaEnginePromise = Promise.resolve(provider()).catch((err) => {
        // Drop the cached rejection so a retried join() can rebuild the engine
        this.mediaEnginePromise = undefined;
        throw err;
      });
    }
    return this.mediaEnginePromise;
  };

  /**
   * Handles the closing of the SFU signal connection.
   *
   * @internal
   * @param sfuClient the SFU client instance that was closed.
   * @param reason the reason for the closure.
   */
  private handleSfuSignalClose = (
    sfuClient: StreamSfuClient,
    reason: string,
  ) => {
    this.logger.debug('[Reconnect] SFU signal connection closed');
    // Ignore a close from a superseded client (e.g. an old socket's delayed
    // `onclose` arriving after a reconnection already swapped in a new client).
    // Only the currently active client's death may drive a reconnection.
    if (sfuClient !== this.sfuClient) return;
    const { callingState } = this.state;
    if (
      // SFU WS closed before we finished current join,
      // no need to schedule reconnecting
      callingState === CallingState.JOINING ||
      // we are already in the process of reconnecting,
      // no need to schedule another one
      callingState === CallingState.RECONNECTING ||
      // SFU WS closed as a result of unsuccessful join,
      // and no further retries need to be made
      callingState === CallingState.IDLE ||
      callingState === CallingState.LEFT
    )
      return;
    // normal close, no need to reconnect
    if (sfuClient.isLeaving || sfuClient.isClosingClean) return;
    // the browser can't reach the network: wait for it rather than spending
    // the reconnect budget. A cleared stale `offline` event means no new one
    // arrives for this outage. Other states are left to `reconnect()`.
    if (
      (callingState === CallingState.JOINED ||
        callingState === CallingState.OFFLINE) &&
      isBrowserOffline()
    ) {
      this.offlineState.goOffline();
      return;
    }

    const strategy =
      this.publisher?.isHealthy() && this.subscriber?.isHealthy()
        ? WebsocketReconnectStrategy.FAST
        : WebsocketReconnectStrategy.REJOIN;
    this.reconnect(strategy, reason).catch((err) => {
      this.logger.warn('[Reconnect] Error reconnecting', err);
    });
  };

  /**
   * Handles the reconnection flow.
   *
   * @internal
   *
   * @param strategy the reconnection strategy to use.
   * @param reason the reason for the reconnection. Pass a `ReconnectReason.*`
   *   constant when the SDK should react to it (e.g.
   *   `ICE_NEVER_CONNECTED` increments the unsupported-network counter).
   */
  private reconnect = async (
    strategy: WebsocketReconnectStrategy,
    reason: ReconnectReason,
  ): Promise<void> => {
    const { callingState } = this.state;
    if (
      callingState === CallingState.JOINING ||
      callingState === CallingState.RECONNECTING ||
      callingState === CallingState.MIGRATING ||
      callingState === CallingState.LEFT ||
      callingState === CallingState.RECONNECTING_FAILED
    )
      return;

    // Drop redundant reconnect calls. If a reconnect is already queued or
    // running for this Call, that entry will resolve whatever broke;
    // queueing more entries just replays the full REJOIN cycle (one extra
    // `POST /join` per entry) once the call is already healthy again.
    if (hasPending(this.reconnectConcurrencyTag)) return;

    // A leave in progress (e.g. the SFU closed the socket on `callEnded`)
    // bumped the generation before this snapshot, so `supersededByLeave`
    // couldn't catch it: a reconnect here would rejoin the call it tears down.
    if (this.leavesInProgress > 0) {
      this.logger.debug('[Reconnect] Leave in progress; not reconnecting');
      return;
    }

    const reconnectLeaveGeneration = this.leaveGeneration;
    const supersededByLeave = () =>
      this.leaveGeneration !== reconnectLeaveGeneration;
    return withoutConcurrency(this.reconnectConcurrencyTag, async () => {
      if (supersededByLeave()) return;
      const reconnectStartTime = Date.now();
      this.reconnectStrategy = strategy;
      this.reconnectReason = reason;
      const sfuRejoinFailures = new Map<string, number>();

      const markAsReconnectingFailed = async () => {
        if (supersededByLeave()) return;
        try {
          // attempt to fetch the call data from the server, as the call
          // state might have changed while we were reconnecting or were offline
          await this.get();
        } finally {
          if (!supersededByLeave()) {
            this.state.setCallingState(CallingState.RECONNECTING_FAILED);
          }
        }
      };

      const giveUpAndLeave = async (message: string) => {
        this.logger.warn(
          `[Reconnect] Giving up: ${message}. Leaving the call.`,
        );
        // If we're mid-iteration, the state can be JOINING; `Call.leave` would
        // then wait for JOINED before proceeding, but no more attempts will run
        // so JOINED never comes. Transition to RECONNECTING so `leave()` proceeds.
        if (this.state.callingState === CallingState.JOINING) {
          this.state.setCallingState(CallingState.RECONNECTING);
        }
        try {
          await this.leave({ message });
        } catch (err) {
          this.logger.warn(`[Reconnect] leave() failed after ${message}`, err);
        }
      };

      // Count this entry into reconnect if it was triggered by a peer
      // connection that never reached `connected`/`completed`.
      if (reason === ReconnectReason.ICE_NEVER_CONNECTED) {
        this.iceFailuresWithoutConnect++;
        if (
          this.iceFailuresWithoutConnect >= this.maxIceFailuresWithoutConnect
        ) {
          await giveUpAndLeave('webrtc_unsupported_network');
          return;
        }
      }

      let attempt = 0;
      do {
        const reconnectingTime = Date.now() - reconnectStartTime;
        const shouldGiveUpReconnecting =
          this.disconnectionTimeoutSeconds > 0 &&
          reconnectingTime / 1000 > this.disconnectionTimeoutSeconds;

        if (shouldGiveUpReconnecting) {
          this.logger.warn(
            '[Reconnect] Stopping reconnection attempts after reaching disconnection timeout',
          );
          await markAsReconnectingFailed();
          return;
        }

        // Rejoin rate limit: bound the number of REJOIN (and MIGRATE)
        // transitions inside a rolling window. FAST is not counted because
        // it does not issue a new backend `joinCall`.
        if (
          this.reconnectStrategy === WebsocketReconnectStrategy.REJOIN ||
          this.reconnectStrategy === WebsocketReconnectStrategy.MIGRATE
        ) {
          if (!this.rejoinRateLimiter.tryRegister()) {
            await giveUpAndLeave('rejoin_attempt_limit_exceeded');
            return;
          }
        }
        // MIGRATE is server-driven (e.g. SFU drains), so it doesn't count
        // toward the long-window backstop.
        if (
          this.reconnectStrategy === WebsocketReconnectStrategy.REJOIN &&
          this.rejoinLongWindowRateLimiter &&
          !this.rejoinLongWindowRateLimiter.tryRegister()
        ) {
          await giveUpAndLeave('rejoin_attempt_limit_exceeded');
          return;
        }

        // we don't increment reconnect attempts for the FAST strategy.
        if (this.reconnectStrategy !== WebsocketReconnectStrategy.FAST) {
          this.reconnectAttempts++;
        }
        const attemptedStrategy = this.reconnectStrategy;
        const currentStrategy = WebsocketReconnectStrategy[attemptedStrategy];
        try {
          // wait until the network is available
          await this.offlineState.networkAvailable;
          if (supersededByLeave()) return;

          this.logger.info(
            `[Reconnect] Reconnecting with strategy ${
              WebsocketReconnectStrategy[this.reconnectStrategy]
            }`,
          );

          switch (this.reconnectStrategy) {
            case WebsocketReconnectStrategy.UNSPECIFIED:
            case WebsocketReconnectStrategy.DISCONNECT:
              this.logger.debug(
                `[Reconnect] No-op strategy ${currentStrategy}`,
              );
              break;
            case WebsocketReconnectStrategy.FAST:
              await this.reconnectFast();
              break;
            case WebsocketReconnectStrategy.REJOIN: {
              const confirmedBadSfus = Array.from(sfuRejoinFailures)
                .filter(([, failures]) => failures >= 2)
                .map(([sfu]) => sfu);

              if (this.joinCallData && confirmedBadSfus.length) {
                this.joinCallData.migrating_from =
                  confirmedBadSfus[confirmedBadSfus.length - 1];
                this.joinCallData.migrating_from_list = confirmedBadSfus;
              }

              try {
                await this.reconnectRejoin();
              } finally {
                delete this.joinCallData?.migrating_from;
                delete this.joinCallData?.migrating_from_list;
              }
              break;
            }
            case WebsocketReconnectStrategy.MIGRATE:
              await this.reconnectMigrate();
              break;
            default:
              ensureExhausted(
                this.reconnectStrategy,
                'Unknown reconnection strategy',
              );
              break;
          }
          // reconnection worked — reset the negotiation-failure streak.
          this.consecutiveNegotiationFailures = 0;
          break; // do-while loop, reconnection worked, exit the loop
        } catch (error) {
          if (supersededByLeave()) return;
          if (attemptedStrategy === WebsocketReconnectStrategy.REJOIN) {
            const failedSfu = this.credentials?.server.edge_name;
            if (failedSfu) {
              const switchSfu =
                error instanceof SfuJoinError &&
                SfuJoinError.isJoinErrorCode(error.errorEvent);
              const failures = (sfuRejoinFailures.get(failedSfu) ?? 0) + 1;
              sfuRejoinFailures.set(
                failedSfu,
                switchSfu ? Math.max(failures, 2) : failures,
              );
            }
          }

          if (isBrowserOffline()) this.offlineState.goOffline();
          if (this.state.callingState === CallingState.OFFLINE) {
            this.logger.debug(
              `[Reconnect] Can't reconnect while offline, stopping reconnection attempts`,
            );
            break;
            // we don't need to handle the error if the call is offline
            // network change event will trigger the reconnection
          }
          if (error instanceof ErrorFromResponse && error.unrecoverable) {
            this.logger.warn(
              `[Reconnect] Can't reconnect due to coordinator unrecoverable error`,
              error,
            );
            await markAsReconnectingFailed();
            return;
          }
          if (error instanceof NegotiationError) {
            this.consecutiveNegotiationFailures++;
            if (
              this.consecutiveNegotiationFailures >=
              this.maxConsecutiveNegotiationFailures
            ) {
              await giveUpAndLeave('repeated_negotiation_failures');
              return;
            }
          }

          // exponential backoff with jitter, capped at 5 s
          await sleep(retryInterval(attempt));
          if (supersededByLeave()) return;

          const wasMigrating =
            this.reconnectStrategy === WebsocketReconnectStrategy.MIGRATE;
          const mustPerformRejoin =
            (Date.now() - reconnectStartTime) / 1000 >
            this.fastReconnectDeadlineSeconds;

          // don't immediately switch to the REJOIN strategy, but instead attempt
          // to reconnect with the FAST strategy for a few times before switching.
          // in some cases, we immediately switch to the REJOIN strategy.
          const shouldRejoin =
            mustPerformRejoin || // if we are past the fast reconnect deadline
            error instanceof FastReconnectRejectedError || // SFU didn't resume
            wasMigrating || // if we were migrating, but the migration failed
            attempt >= 3 || // after 3 failed attempts
            !(this.publisher?.isHealthy() ?? true) || // if the publisher is not healthy
            !(this.subscriber?.isHealthy() ?? true); // if the subscriber is not healthy

          attempt++;

          const nextStrategy = shouldRejoin
            ? WebsocketReconnectStrategy.REJOIN
            : WebsocketReconnectStrategy.FAST;
          this.reconnectStrategy = nextStrategy;

          this.logger.info(
            `[Reconnect] ${currentStrategy} (${this.reconnectAttempts}) failed. Attempting with ${WebsocketReconnectStrategy[nextStrategy]}`,
            error,
          );
        }
      } while (
        !supersededByLeave() &&
        this.state.callingState !== CallingState.JOINED &&
        this.state.callingState !== CallingState.RECONNECTING_FAILED &&
        this.state.callingState !== CallingState.LEFT
      );
      this.logger.info('[Reconnect] Reconnection flow finished');
    });
  };

  /**
   * Initiates the reconnection flow with the "fast" strategy.
   * @internal
   */
  private reconnectFast = async () => {
    const reconnectLeaveGeneration = this.leaveGeneration;
    const reconnectStartTime = Date.now();
    this.reconnectStrategy = WebsocketReconnectStrategy.FAST;
    this.state.setCallingState(CallingState.RECONNECTING);
    await this.doJoin(this.joinCallData);
    if (this.leaveGeneration !== reconnectLeaveGeneration) return;
    // The SFU restarts the subscriber by sending an offer after the join. If
    // none comes, the session wasn't resumed and the call would stay silent
    // while JOINED: rejoin. Awaited here rather than reported through
    // `onReconnectionNeeded`, which `reconnect()` drops while this flow runs,
    // and before `get()`, so a coordinator hiccup can't skip the check.
    const offerReceived = await this.fastReconnectOffer;
    this.fastReconnectOffer = undefined;
    if (this.leaveGeneration !== reconnectLeaveGeneration) return;
    if (offerReceived === false) {
      this.state.setCallingState(CallingState.RECONNECTING);
      throw new FastReconnectRejectedError(
        'No subscriber offer after a fast reconnect',
      );
    }
    await this.get(); // fetch the latest call state, as it might have changed
    this.sfuStatsReporter?.sendReconnectionTime(
      WebsocketReconnectStrategy.FAST,
      (Date.now() - reconnectStartTime) / 1000,
    );
  };

  /**
   * Initiates the reconnection flow with the "rejoin" strategy.
   * @internal
   */
  private reconnectRejoin = async () => {
    const reconnectLeaveGeneration = this.leaveGeneration;
    const reconnectStartTime = Date.now();
    this.reconnectStrategy = WebsocketReconnectStrategy.REJOIN;
    this.state.setCallingState(CallingState.RECONNECTING);
    const joinReason: JoinReason =
      this.reconnectReason === ReconnectReason.NETWORK_BACK_ONLINE
        ? 'network-available'
        : 'full-rejoin';
    await this.clientEventReporter.withJoinLifecycle(
      this.cid,
      { joinReason },
      () => this.doJoin(this.joinCallData),
    );
    if (this.leaveGeneration !== reconnectLeaveGeneration) return;
    await this.restorePublishedTracks();
    if (this.leaveGeneration !== reconnectLeaveGeneration) return;
    this.restoreSubscribedTracks();
    this.sfuStatsReporter?.sendReconnectionTime(
      WebsocketReconnectStrategy.REJOIN,
      (Date.now() - reconnectStartTime) / 1000,
    );
  };

  /**
   * Initiates the reconnection flow with the "migrate" strategy.
   * @internal
   */
  private reconnectMigrate = async () => {
    const reconnectLeaveGeneration = this.leaveGeneration;
    const reconnectStartTime = Date.now();
    const currentSfuClient = this.sfuClient;
    if (!currentSfuClient) {
      throw new Error('Cannot migrate without an active SFU client');
    }

    this.reconnectStrategy = WebsocketReconnectStrategy.MIGRATE;
    this.state.setCallingState(CallingState.MIGRATING);
    const currentSubscriber = this.subscriber;
    const currentPublisher = this.publisher;

    currentSubscriber?.detachEventHandlers();
    currentPublisher?.detachEventHandlers();

    const migrationTask = makeSafePromise(currentSfuClient.enterMigration());

    try {
      try {
        const currentSfu = currentSfuClient.edgeName;
        await this.clientEventReporter.withJoinLifecycle(
          this.cid,
          { joinReason: 'migration' },
          () =>
            this.doJoin({
              ...this.joinCallData,
              migrating_from: currentSfu,
              migrating_from_list: [currentSfu],
            }),
        );
      } finally {
        // cleanup the migration_from field after the migration is complete or failed
        // as we don't want to keep dirty data in the join call data
        delete this.joinCallData?.migrating_from;
        delete this.joinCallData?.migrating_from_list;
      }

      if (this.leaveGeneration !== reconnectLeaveGeneration) return;
      await this.restorePublishedTracks();
      if (this.leaveGeneration !== reconnectLeaveGeneration) return;
      this.restoreSubscribedTracks();

      // Wait for the migration to complete, then close the previous SFU client
      // and the peer connection instances. In case of failure, the migration
      // task would throw an error and REJOIN would be attempted.
      await migrationTask();
      if (this.leaveGeneration !== reconnectLeaveGeneration) return;

      // in MIGRATE, we can consider the call as joined only after
      // `participantMigrationComplete` event is received, signaled by
      // the `migrationTask`
      this.state.setCallingState(CallingState.JOINED);
    } finally {
      await currentSubscriber?.dispose();
      await currentPublisher?.dispose();

      // and close the previous SFU client, without specifying close code
      currentSfuClient.close(StreamSfuClient.NORMAL_CLOSURE, 'Migrating away');
    }
    this.sfuStatsReporter?.sendReconnectionTime(
      WebsocketReconnectStrategy.MIGRATE,
      (Date.now() - reconnectStartTime) / 1000,
    );
  };

  /**
   * Registers the various event handlers for reconnection.
   *
   * @internal
   */
  private registerReconnectHandlers = () => {
    // handles the legacy "goAway" event
    const unregisterGoAway = this.on('goAway', () => {
      this.clientEventReporter.captureWsError(this.cid, {
        code: 'SFU_GO_AWAY',
        reason: 'SFU goAway received during WS join',
      });
      this.reconnect(
        WebsocketReconnectStrategy.MIGRATE,
        ReconnectReason.GO_AWAY,
      ).catch((err) => this.logger.warn('[Reconnect] Error reconnecting', err));
    });

    // handles the "error" event, through which the SFU can request a reconnect
    // Only the active SFU client may drive a reconnect: a superseded one can
    // still report errors about its own (replaced) session, and none may
    // while `leave()` tears the call down.
    const activeSfuTag = () => this.sfuClient?.tag ?? '*';
    const unregisterOnError = this.dispatcher.on('error', activeSfuTag, (e) => {
      if (!this.sfuClient) return;
      const { reconnectStrategy: strategy, error } = e;
      if (!SfuJoinError.isJoinErrorCode(e)) {
        const code = error?.code ? ErrorCode[error.code] : undefined;
        this.clientEventReporter.captureWsError(this.cid, {
          code: code ?? 'SFU_ERROR',
          reason: error?.message || 'SFU error during WS join',
        });
      }
      // SFU_FULL is a join error, and when emitted, although it specifies a
      // `migrate` strategy, we should actually perform a REJOIN to a new SFU.
      // This is now handled separately in the `call.join()` method.
      if (SfuJoinError.isJoinErrorCode(e)) return;
      if (strategy === WebsocketReconnectStrategy.UNSPECIFIED) return;
      if (strategy === WebsocketReconnectStrategy.DISCONNECT) {
        globalThis.streamRNVideoSDK?.callingX?.endCall(this, 'error');
        this.leave({ message: 'SFU instructed to disconnect' }).catch((err) => {
          this.logger.warn(`Can't leave call after disconnect request`, err);
        });
      } else {
        // A media transport failure on a session whose peer connections
        // never connected is an "ICE never connected" failure, and must
        // count toward the unsupported-network budget.
        const neverConnected =
          error?.code === ErrorCode.PARTICIPANT_MEDIA_TRANSPORT_FAILURE &&
          !this.publisher?.hasIceEverConnected() &&
          !this.subscriber?.hasIceEverConnected();
        const reason = neverConnected
          ? ReconnectReason.ICE_NEVER_CONNECTED
          : error?.message || ReconnectReason.SFU_ERROR;
        this.reconnect(strategy, reason).catch((err) => {
          this.logger.warn('[Reconnect] Error reconnecting', err);
        });
      }
    });

    const unregisterNetworkChanged = this.streamClient.on(
      'network.changed',
      (e) => {
        this.tracer.trace('network.changed', e);
        if (!e.online) {
          this.logger.debug('[Reconnect] Going offline');
          if (!this.hasJoinedOnce) return;
        }
        this.offlineState.handleNetworkChanged(e.online);
      },
    );

    this.leaveCallHooks
      .add(unregisterGoAway)
      .add(unregisterOnError)
      .add(unregisterNetworkChanged);
  };

  /**
   * Reconnects once the network is back after being offline for `offlineMs`.
   */
  private reconnectAfterOffline = (offlineMs: number) => {
    // We shouldn't attempt FAST if we have exceeded the deadline.
    // The SFU would have already wiped out the session.
    const strategy =
      offlineMs / 1000 > this.fastReconnectDeadlineSeconds
        ? WebsocketReconnectStrategy.REJOIN
        : WebsocketReconnectStrategy.FAST;
    this.reconnect(strategy, ReconnectReason.NETWORK_BACK_ONLINE).catch(
      (err) => {
        this.logger.warn(
          '[Reconnect] Error reconnecting after going online',
          err,
        );
      },
    );
  };

  /**
   * Restores the published tracks after a reconnection.
   * @internal
   */
  private restorePublishedTracks = async () => {
    // the tracks need to be restored in their original order of publishing
    // otherwise, we might get `m-lines order mismatch` errors
    for (const trackType of this.trackPublishOrder) {
      let mediaStream: MediaStream | undefined;
      let options: TrackPublishOptions | undefined;
      switch (trackType) {
        case TrackType.AUDIO:
          mediaStream = this.microphone.state.mediaStream;
          options = {
            audioBitrateProfile: this.microphone.state.audioBitrateProfile,
          };
          break;
        case TrackType.VIDEO:
          mediaStream = this.camera.state.mediaStream;
          break;
        case TrackType.SCREEN_SHARE:
          mediaStream = this.screenShare.state.mediaStream;
          options = {
            audioBitrateProfile: this.screenShare.state.audioBitrateProfile,
            contentHint: this.screenShare.resolveContentHint(),
          };
          break;
        // screen share audio can't exist without a screen share, so we handle it there
        case TrackType.SCREEN_SHARE_AUDIO:
        case TrackType.UNSPECIFIED:
          break;
        default:
          ensureExhausted(trackType, 'Unknown track type');
          break;
      }

      if (mediaStream) await this.publish(mediaStream, trackType, options);
    }
  };

  /**
   * Restores the subscribed tracks after a reconnection.
   * @internal
   */
  private restoreSubscribedTracks = () => {
    const { remoteParticipants } = this.state;
    if (remoteParticipants.length <= 0) return;
    this.trackSubscriptionManager.apply(undefined);
  };

  /**
   * Publishes the given media stream.
   *
   * @param mediaStream the media stream to publish.
   * @param trackType the type of the track to announce.
   * @param options the publish options.
   */
  publish = async (
    mediaStream: MediaStream,
    trackType: TrackType,
    options?: TrackPublishOptions,
  ) => {
    if (!this.sfuClient) throw new Error(`Call is not joined yet`);
    // joining is in progress, and we should wait until the client is ready
    await this.sfuClient.joinTask;

    if (!this.permissionsContext.canPublish(trackType)) {
      throw new Error(`No permission to publish ${TrackType[trackType]}`);
    }

    if (!this.publisher) throw new Error('Publisher is not initialized');

    const [track] = isAudioTrackType(trackType)
      ? mediaStream.getAudioTracks()
      : mediaStream.getVideoTracks();

    if (!track) {
      throw new Error(
        `There is no ${TrackType[trackType]} track in the stream`,
      );
    }

    if (track.readyState === 'ended') {
      throw new Error(`Can't publish ended tracks.`);
    }

    pushToIfMissing(this.trackPublishOrder, trackType);
    await this.publisher.publish(track, trackType, options);

    const trackTypes = [trackType];
    if (trackType === TrackType.SCREEN_SHARE) {
      const [audioTrack] = mediaStream.getAudioTracks();
      if (audioTrack) {
        const screenShareAudio = TrackType.SCREEN_SHARE_AUDIO;
        pushToIfMissing(this.trackPublishOrder, screenShareAudio);
        await this.publisher.publish(audioTrack, screenShareAudio, options);
        trackTypes.push(screenShareAudio);
      }
    }

    await this.updateLocalStreamState(mediaStream, ...trackTypes);
  };

  /**
   * Stops publishing the given track type to the call, if it is currently being published.
   *
   * @param trackTypes the track types to stop publishing.
   */
  stopPublish = async (...trackTypes: TrackType[]) => {
    if (!this.sfuClient || !this.publisher) return;
    await this.publisher.stopTracks(...trackTypes);
    await this.updateLocalStreamState(undefined, ...trackTypes);
  };

  /**
   * Updates the call state with the new stream.
   *
   * @param mediaStream the new stream to update the call state with.
   * If undefined, the stream will be removed from the call state.
   * @param trackTypes the track types to update the call state with.
   */
  private updateLocalStreamState = async (
    mediaStream: MediaStream | undefined,
    ...trackTypes: TrackType[]
  ) => {
    const sessionId = this.sfuClient?.sessionId;
    if (!sessionId) return;

    await this.notifyTrackMuteState(!mediaStream, ...trackTypes);
    if (this.sfuClient?.sessionId !== sessionId) return;

    for (const trackType of trackTypes) {
      const streamStateProp = trackTypeToParticipantStreamKey(trackType);
      if (!streamStateProp) continue;

      this.state.updateParticipant(sessionId, (p) => {
        const currentStream = p[streamStateProp];
        const isEchoedStream =
          this.subscriber?.isSelfSubscribedStream(currentStream);

        return {
          publishedTracks: mediaStream
            ? pushToIfMissing([...p.publishedTracks], trackType)
            : p.publishedTracks.filter((t) => t !== trackType),
          [streamStateProp]: isEchoedStream ? currentStream : mediaStream,
        };
      });
    }
  };

  /**
   * Re-arms the encoder for a currently published track type. Useful for
   * working around WebKit's stalled sender bug after an iOS audio session
   * interruption (Siri, PSTN call).
   *
   * @internal
   *
   * @param trackType the track type to refresh.
   */
  refreshPublishedTrack = async (trackType: TrackType) => {
    if (!this.publisher) return;
    await this.publisher.refreshTrack(trackType);
  };

  /**
   * Updates the preferred publishing options
   *
   * @internal
   * @param options the options to use.
   */
  updatePublishOptions = (options: ClientPublishOptions) => {
    this.logger.warn(
      '[call.updatePublishOptions]: You are manually overriding the publish options for this call. ' +
        'This is not recommended, and it can cause call stability/compatibility issues. Use with caution.',
    );
    if (this.state.callingState === CallingState.JOINED) {
      this.logger.warn(
        'Updating publish options after joining the call does not have an effect',
      );
    }
    this.tracer.trace('updatePublishOptions', options);
    this.clientPublishOptions = { ...this.clientPublishOptions, ...options };
  };

  /**
   * Set the E2EE (end-to-end encryption) manager for this call.
   *
   * Must be called before {@link join} so the RTCPeerConnection can be
   * configured for E2EE.
   *
   * The application owns the manager's lifetime - the SDK never disposes it, and
   * closing the peer connections does not release it. Discard the manager
   * together with this call instance once its flow has ended, and create a fresh
   * pair for a later flow: a disposed manager throws from `encrypt`/`decrypt`
   * rather than silently publishing nothing.
   *
   * @param e2ee - Any `E2EEManager`. Use `EncryptionManager.create()` for the
   *         built-in AES-GCM scheme, or pass your own implementation.
   * @throws if called after the peer connections have been built (i.e. after
   *         `join`): those PCs were already configured without an encryptor, so
   *         adopting a manager now would silently publish/receive cleartext for
   *         the live session.
   */
  setE2EEManager = (e2ee: E2EEManager) => {
    if (this.publisher || this.subscriber) {
      throw new Error('setE2EEManager must be called before join()');
    }
    this.e2eeManager = e2ee;
  };

  /**
   * Notifies the SFU that a noise cancellation process has started.
   *
   * @internal
   */
  notifyNoiseCancellationStarting = async () => {
    return this.sfuClient?.startNoiseCancellation().catch((err) => {
      this.logger.warn('Failed to notify start of noise cancellation', err);
    });
  };

  /**
   * Notifies the SFU that a noise cancellation process has stopped.
   *
   * @internal
   */
  notifyNoiseCancellationStopped = async () => {
    return this.sfuClient?.stopNoiseCancellation().catch((err) => {
      this.logger.warn('Failed to notify stop of noise cancellation', err);
    });
  };

  /**
   * Notifies the SFU about the mute state of the given track types.
   * @internal
   */
  notifyTrackMuteState = async (muted: boolean, ...trackTypes: TrackType[]) => {
    const key = `muteState.${this.cid}.${trackTypes.join('-')}`;
    await withoutConcurrency(key, async () => {
      if (!this.sfuClient) return;
      await this.sfuClient.updateMuteStates(
        trackTypes.map((trackType) => ({ trackType, muted })),
      );
    });
  };

  /**
   * Will enhance the reported stats with additional participant-specific information (the `callStatsReport$` state variable).
   * This is usually helpful when detailed stats for a specific participant are needed.
   *
   * @param sessionId the sessionId to start reporting for.
   */
  startReportingStatsFor = (sessionId: string) => {
    return this.statsReporter?.startReportingStatsFor(sessionId);
  };

  /**
   * Opposite of `startReportingStatsFor`.
   * Will turn off stats reporting for a specific participant.
   *
   * @param sessionId the sessionId to stop reporting for.
   */
  stopReportingStatsFor = (sessionId: string) => {
    return this.statsReporter?.stopReportingStatsFor(sessionId);
  };

  /**
   * Sets the frequency of the call stats reporting.
   *
   * @param intervalInMs the interval in milliseconds to report the stats.
   */
  setStatsReportingIntervalInMs = (intervalInMs: number) => {
    this.statsReportingIntervalInMs = intervalInMs;
  };

  /**
   * Resets the last sent reaction for the user holding the given `sessionId`. This is a local action, it won't reset the reaction on the backend.
   *
   * @param sessionId the session id.
   */
  resetReaction = (sessionId: string) => {
    this.state.updateParticipant(sessionId, {
      reaction: undefined,
    });
  };

  /**
   * Sets the list of criteria to sort the participants by.
   *
   * @param criteria the list of criteria to sort the participants by.
   */
  setSortParticipantsBy: CallState['setSortParticipantsBy'] = (criteria) => {
    return this.state.setSortParticipantsBy(criteria);
  };

  /**
   * Returns the comparator currently used to sort the participants.
   */
  getSortParticipantsBy: CallState['getSortParticipantsBy'] = () => {
    return this.state.getSortParticipantsBy();
  };

  /**
   * Sends a reaction to the other call participants.
   *
   * @param reaction the reaction to send.
   */
  sendReaction = async (reaction: SendVideoReactionRequest) => {
    return this.api.sendVideoReaction(reaction);
  };

  /**
   * Mutes the current user.
   *
   * @param type the type of the mute operation.
   */
  muteSelf = (type: TrackMuteType) => {
    const myUserId = this.currentUserId;
    if (!myUserId) return undefined;
    return this.muteUser(myUserId, type);
  };

  /**
   * Mutes all the other participants.
   *
   * @param type the type of the mute operation.
   */
  muteOthers = (type: TrackMuteType) => {
    const trackType = muteTypeToTrackType(type);
    if (!trackType) return;
    const userIdsToMute: string[] = [];
    for (const participant of this.state.remoteParticipants) {
      if (participant.publishedTracks.includes(trackType)) {
        userIdsToMute.push(participant.userId);
      }
    }

    return userIdsToMute.length > 0
      ? this.muteUser(userIdsToMute, type)
      : undefined;
  };

  /**
   * Mutes the user with the given `userId`.
   *
   * @param userId the id of the user to mute.
   * @param type the type of the mute operation.
   */
  muteUser = (userId: string | string[], type: TrackMuteType) => {
    return this.api.muteUsers({
      user_ids: Array.isArray(userId) ? userId : [userId],
      [type]: true,
    });
  };

  /**
   * Will mute all users in the call.
   *
   * @param type the type of the mute operation.
   */
  muteAllUsers = (type: TrackMuteType) => {
    return this.api.muteUsers({ mute_all_users: true, [type]: true });
  };

  /**
   * Starts recording the call.
   */
  startRecording = ({
    recording_type = 'composite',
    ...request
  }: StartRecordingRequest & {
    /** The kind of recording to start, `'composite'` by default. */
    recording_type?: CallRecordingType;
  } = {}) => {
    return this.api.startRecording({ recording_type }, request);
  };

  /**
   * Stops recording the call.
   */
  stopRecording = ({
    recording_type = 'composite',
  }: {
    /** The kind of recording to stop, `'composite'` by default. */
    recording_type?: CallRecordingType;
  } = {}) => {
    return this.api.stopRecording({ recording_type });
  };

  /**
   * Starts the closed captions of the call.
   */
  startClosedCaptions = async (options?: StartClosedCaptionsRequest) => {
    const trx = this.state.setCaptioning(true); // optimistic update
    try {
      return await this.api.startClosedCaptions(options);
    } catch (err) {
      trx.rollback(); // revert the optimistic update
      throw err;
    }
  };

  /**
   * Stops the closed captions of the call.
   */
  stopClosedCaptions = async (options?: StopClosedCaptionsRequest) => {
    const trx = this.state.setCaptioning(false); // optimistic update
    try {
      return await this.api.stopClosedCaptions(options);
    } catch (err) {
      trx.rollback(); // revert the optimistic update
      throw err;
    }
  };

  /**
   * Updates the closed caption settings.
   *
   * @param config the closed caption settings to apply
   */
  updateClosedCaptionSettings = (config: Partial<ClosedCaptionsSettings>) => {
    this.state.updateClosedCaptionSettings(config);
  };

  /**
   * Sends a `call.permission_request` event to all users connected to the call.
   * The call settings object contains information about which permissions can be requested during a call
   * (for example, a user might be allowed to request permission to publish audio, but not video).
   */
  requestPermissions = async (
    data: Omit<RequestPermissionRequest, 'permissions'> & {
      permissions: OwnCapability[];
    },
  ): Promise<StreamResponse<RequestPermissionResponse>> => {
    const { permissions } = data;
    const canRequestPermissions = permissions.every((permission) =>
      this.permissionsContext.canRequest(permission),
    );
    if (!canRequestPermissions) {
      throw new Error(
        `You are not allowed to request permissions: ${permissions.join(', ')}`,
      );
    }
    return this.api.requestPermission(data);
  };

  /**
   * Allows you to grant certain permissions to a user in a call.
   * The permissions are specific to the call experience and do not survive the call itself.
   *
   * Supported permissions that can be granted are:
   * - `send-audio`
   * - `send-video`
   * - `screenshare`
   *
   * @param userId the id of the user to grant permissions to.
   * @param permissions the permissions to grant.
   */
  grantPermissions = async (
    userId: string,
    permissions: OwnCapability[] | string[],
  ) => {
    return this.updateUserPermissions({
      user_id: userId,
      grant_permissions: permissions,
    });
  };

  /**
   * Allows you to revoke certain permissions from a user in a call.
   * The permissions are specific to the call experience and do not survive the call itself.
   *
   * Supported permissions that can be revoked are:
   * - `send-audio`
   * - `send-video`
   * - `screenshare`
   *
   * @param userId the id of the user to revoke permissions from.
   * @param permissions the permissions to revoke.
   */
  revokePermissions = async (
    userId: string,
    permissions: OwnCapability[] | string[],
  ) => {
    return this.updateUserPermissions({
      user_id: userId,
      revoke_permissions: permissions,
    });
  };

  /**
   * Starts the broadcasting of the call.
   */
  startHLS = async () => {
    return this.api.startHLSBroadcasting();
  };

  /**
   * Stops the broadcasting of the call.
   */
  stopHLS = async () => {
    return this.api.stopHLSBroadcasting();
  };

  /**
   * Updates the call settings or custom data.
   *
   * @param updates the updates to apply to the call.
   */
  update = async (updates?: UpdateCallRequest) => {
    const response = await this.api.update(updates);

    const { call, members, own_capabilities } = response;
    this.state.updateFromCallResponse(call);
    this.state.setMembers(members);
    this.state.setOwnCapabilities(own_capabilities);

    return response;
  };

  /**
   * Ends the call. Once the call is ended, it cannot be re-joined.
   */
  endCall = async () => {
    return this.api.end();
  };

  /**
   * Pins the given session to the top of the participants list.
   *
   * @param sessionId the sessionId to pin.
   */
  pin = (sessionId: string) => {
    this.state.updateParticipant(sessionId, {
      pin: {
        isLocalPin: true,
        pinnedAt: Date.now(),
      },
    });
  };

  /**
   * Unpins the given session from the top of the participants list.
   *
   * @param sessionId the sessionId to unpin.
   */
  unpin = (sessionId: string) => {
    this.state.updateParticipant(sessionId, {
      pin: undefined,
    });
  };

  /**
   * Pins the given session to the top of the participants list for everyone
   * in the call.
   * You can execute this method only if you have the `pin-for-everyone` capability.
   *
   * @param request the request object.
   */
  pinForEveryone = async (request: PinRequest) => {
    return this.api.videoPin(request);
  };

  /**
   * Unpins the given session from the top of the participants list for everyone
   * in the call.
   * You can execute this method only if you have the `pin-for-everyone` capability.
   *
   * @param request the request object.
   */
  unpinForEveryone = async (request: UnpinRequest) => {
    return this.api.videoUnpin(request);
  };

  /**
   * Query call members with filter query. The result won't be stored in call state.
   * @param request
   * @returns
   */
  queryMembers = (request?: Omit<QueryCallMembersRequest, 'type' | 'id'>) => {
    return this.videoApi.queryCallMembers({
      ...(request || {}),
      id: this.id,
      type: this.type,
    });
  };

  /**
   * Queries the call participants that match the filter.
   *
   * @param data.filter_conditions which participants to return (required by the API).
   * @param data.limit the maximum number of participants to return.
   */
  queryParticipants = async (
    data: QueryCallParticipantsRequest &
      Required<Pick<QueryCallParticipantsRequest, 'filter_conditions'>> & {
        limit?: number;
      },
  ) => {
    return this.api.queryCallParticipants(data);
  };

  /**
   * Schedules an auto-drop timeout based on the call settings.
   * Applicable only for ringing calls.
   */
  private scheduleAutoDrop = () => {
    this.cancelAutoDrop();
    this.ringTimeout = new RingTimeout(this);
    this.ringTimeout.start();
  };

  /**
   * Cancels a scheduled auto-drop timeout.
   */
  private cancelAutoDrop = () => {
    this.ringTimeout?.stop();
    this.ringTimeout = undefined;
  };

  /**
   * Starts polling for the ring outcome. Applicable only to ringing calls the
   * current user created.
   */
  private scheduleRingStatePolling = () => {
    this.cancelRingStatePolling();

    if (!this.isCreatedByMe) return;
    const options = this.streamClient.options.ringStatePolling;
    if (options === false) return;

    this.ringStatePoller = new RingStatePoller(this, options);
    this.ringStatePoller.start();
  };

  /**
   * Cancels the ring state polling.
   */
  private cancelRingStatePolling = () => {
    this.ringStatePoller?.stop();
    this.ringStatePoller = undefined;
  };

  /**
   * Loads the call participant stats for the given parameters.
   */
  getCallParticipantsStats = async (opts: {
    sessionId?: string;
    userId?: string;
    userSessionId?: string;
    kind?: 'timeline' | 'details';
  }): Promise<
    | StreamResponse<QueryCallSessionParticipantStatsResponse>
    | StreamResponse<GetCallSessionParticipantStatsDetailsResponse>
    | StreamResponse<QueryCallSessionParticipantStatsTimelineResponse>
    | undefined
  > => {
    const {
      sessionId = this.state.session?.id,
      userId = this.currentUserId,
      userSessionId = this.unifiedSessionId,
      kind = 'details',
    } = opts;
    if (!sessionId) return;
    const scope = {
      call_type: this.type,
      call_id: this.id,
      session: sessionId,
    };
    if (!userId || !userSessionId) {
      return this.videoApi.queryCallSessionParticipantStats(scope);
    }
    const participant = {
      ...scope,
      user: userId,
      user_session: userSessionId,
    };
    if (kind === 'details') {
      return this.videoApi.getCallSessionParticipantStatsDetails(participant);
    }
    return this.videoApi.getCallSessionParticipantStatsTimeline(participant);
  };

  /**
   * Submit user feedback for the call
   */
  submitFeedback = async ({
    rating,
    reason,
    custom,
  }: {
    /** Rating between 1 and 5 denoting the experience of the user in the call. */
    rating: CollectUserFeedbackRequest['rating'];
    /** The reason/description for the rating. */
    reason?: CollectUserFeedbackRequest['reason'];
    /** Custom data. */
    custom?: CollectUserFeedbackRequest['custom'];
  }): Promise<StreamResponse<CollectUserFeedbackResponse>> => {
    const { sdkName, sdkVersion, ...platform } = getSdkSignature(
      await getClientDetails(),
    );
    return this.api.collectUserFeedback({
      rating,
      reason,
      user_session_id: this.sfuClient?.sessionId,
      sdk: sdkName,
      sdk_version: sdkVersion,
      custom: {
        ...custom,
        'x-stream-platform-data': platform,
      },
    });
  };

  /**
   * Retrieves the call stats for the current call session in a format suitable
   * for displaying in map-like UIs.
   */
  getCallStatsMap = async ({
    session = this.state.session?.id,
    ...params
  }: {
    session?: string;
    start_time?: Date;
    end_time?: Date;
    exclude_publishers?: boolean;
    exclude_subscribers?: boolean;
    exclude_sfus?: boolean;
  } = {}): Promise<StreamResponse<QueryCallStatsMapResponse>> => {
    if (!session) throw new Error('session is required');
    return this.videoApi.getCallStatsMap(
      { call_type: this.type, call_id: this.id, session },
      params,
    );
  };

  /**
   * Sends a custom event to all call participants.
   *
   * @param payload the payload to send.
   */
  sendCustomEvent = async (payload: { [key: string]: any }) => {
    return this.api.sendCallEvent({ custom: payload });
  };

  /**
   * Deletes the call.
   */
  delete = (data: DeleteCallRequest = {}) => {
    return this.api.delete(data);
  };

  /**
   * Starts the livestream of the call.
   */
  goLive = (data: GoLiveRequest = {}) => {
    return this.api.goLive(data);
  };

  /**
   * Kicks a user from the call.
   */
  kickUser = (data: KickUserRequest) => {
    return this.api.kickUser(data);
  };

  /**
   * Lists the recordings of the call.
   */
  listRecordings = () => {
    return this.api.listRecordings();
  };

  /**
   * Lists the transcriptions of the call.
   */
  listTranscriptions = () => {
    return this.api.listTranscriptions();
  };

  /**
   * Rings the call, notifying its members.
   */
  ring = (data: RingCallRequest = {}) => {
    return this.api.ring(data);
  };

  /**
   * Starts frame recording of the call.
   */
  startFrameRecording = (data: StartFrameRecordingRequest) => {
    return this.api.startFrameRecording(data);
  };

  /**
   * Starts RTMP broadcasts of the call.
   */
  startRTMPBroadcasts = (data: StartRTMPBroadcastsRequest) => {
    return this.api.startRTMPBroadcasts(data);
  };

  /**
   * Starts transcribing the call.
   */
  startTranscription = (request?: StartTranscriptionRequest) => {
    return this.api.startTranscription(request);
  };

  /**
   * Stops all RTMP broadcasts of the call.
   */
  stopAllRTMPBroadcasts = () => {
    return this.api.stopAllRTMPBroadcasts();
  };

  /**
   * Stops frame recording of the call.
   */
  stopFrameRecording = () => {
    return this.api.stopFrameRecording();
  };

  /**
   * Stops the livestream of the call.
   */
  stopLive = (data: StopLiveRequest = {}) => {
    return this.api.stopLive(data);
  };

  /**
   * Stops transcribing the call.
   */
  stopTranscription = (request?: StopTranscriptionRequest) => {
    return this.api.stopTranscription(request);
  };

  /**
   * Updates the members of the call.
   */
  updateCallMembers = (data: UpdateCallMembersRequest) => {
    return this.api.updateCallMembers(data);
  };

  /**
   * Grants or revokes permissions for a user.
   */
  updateUserPermissions = (data: UpdateUserPermissionsRequest) => {
    return this.api.updateUserPermissions(data);
  };

  /**
   * Blocks a user from the call.
   *
   * @param data.user_id the id of the user to block.
   */
  blockUser = (data: BlockUserRequest) => {
    return this.api.blockUser(data);
  };

  /**
   * Unblocks a previously blocked user.
   *
   * @param data.user_id the id of the user to unblock.
   */
  unblockUser = (data: UnblockUserRequest) => {
    return this.api.unblockUser(data);
  };

  /**
   * Deletes a recording of the call.
   *
   * @param data.session the session the recording belongs to.
   * @param data.filename the name of the recording to delete.
   */
  deleteRecording = (data: { session: string; filename: string }) => {
    return this.api.deleteRecording(data);
  };

  /**
   * Deletes a transcription of the call.
   *
   * @param data.session the session the transcription belongs to.
   * @param data.filename the name of the transcription to delete.
   */
  deleteTranscription = (data: { session: string; filename: string }) => {
    return this.api.deleteTranscription(data);
  };

  /**
   * Returns the report of the call, optionally scoped to one session.
   *
   * @param data.session_id the session to report on; the current session, or
   * the most recent one, when omitted.
   */
  getCallReport = (data: { session_id?: string } = {}) => {
    return this.api.getCallReport(data);
  };

  /**
   * Stops a single RTMP broadcast of the call.
   */
  stopRTMPBroadcast = ({ name }: { name: string }) => {
    return this.api.stopRTMPBroadcast({ name });
  };

  /**
   * Applies the device configuration from the backend.
   *
   * @internal
   */
  applyDeviceConfig = async (
    settings: CallSettingsResponse,
    opts: {
      publish: boolean;
      withDisabledDevices?: boolean;
    },
  ) => {
    const { publish, withDisabledDevices } = opts;
    await this.speaker
      .apply()
      .catch((err) => this.logger.warn('Speaker init failed', err));
    await this.camera
      .apply(settings.video, publish, withDisabledDevices)
      .catch((err) => this.logger.warn('Camera init failed', err));
    await this.microphone
      .apply(settings.audio, publish, withDisabledDevices)
      .catch((err) => this.logger.warn('Mic init failed', err));
  };

  /**
   * Will begin tracking the given element for visibility changes within the
   * configured viewport element (`call.setViewport`).
   *
   * @param element the element to track.
   * @param sessionId the session id.
   * @param trackType the video mode.
   */
  trackElementVisibility = <T extends HTMLElement>(
    element: T,
    sessionId: string,
    trackType: VideoTrackType,
  ) => {
    return this.viewportTracker?.trackElementVisibility(
      element,
      sessionId,
      trackType,
    );
  };

  /**
   * Sets the viewport element to track bound video elements for visibility.
   *
   * @param element the viewport element.
   */
  setViewport = <T extends HTMLElement>(element: T) => {
    return this.viewportTracker?.setViewport(element);
  };

  /**
   * Binds a DOM <video> element to the given session id.
   * This method will make sure that the video element will play
   * the correct video stream for the given session id.
   *
   * Under the hood, it would also keep track of the video element dimensions
   * and update the subscription accordingly in order to optimize the bandwidth.
   *
   * If a "viewport" is configured, the video element will be automatically
   * tracked for visibility and the subscription will be updated accordingly.
   *
   * @param videoElement the video element to bind to.
   * @param sessionId the session id.
   * @param trackType the kind of video.
   */
  bindVideoElement = (
    videoElement: HTMLVideoElement,
    sessionId: string,
    trackType: VideoTrackType,
  ) => {
    const unbindDynascale = this.dynascaleManager?.bindVideoElement(
      videoElement,
      sessionId,
      trackType,
    );

    const stopFirstFrameDetector = this.bindFirstVideoFrameDetector(
      videoElement,
      sessionId,
      trackType,
    );

    if (!unbindDynascale && !stopFirstFrameDetector) return;

    const unbind = () => {
      stopFirstFrameDetector?.();
      unbindDynascale?.();
    };

    this.leaveCallHooks.add(unbind);
    return () => {
      this.leaveCallHooks.delete(unbind);
      unbind();
    };
  };

  private bindFirstVideoFrameDetector = (
    videoElement: HTMLVideoElement,
    sessionId: string,
    trackType: VideoTrackType,
  ) => {
    if (trackType !== 'videoTrack') return;

    return createFirstVideoFrameDetector(videoElement, () => {
      this.reportFirstRenderedVideoFrame(sessionId);
    });
  };

  private reportFirstRenderedVideoFrame = (sessionId: string) => {
    const participant = this.state.findParticipantBySessionId(sessionId);
    if (participant?.isLocalParticipant) return;

    const trackId = participant?.videoStream?.getVideoTracks()[0]?.id;
    if (!trackId) return;

    this.clientEventReporter.reportFirstFrame(
      this.cid,
      TrackType.VIDEO,
      trackId,
    );
  };

  /**
   * Binds a DOM <audio> element to the given session id.
   *
   * This method will make sure that the audio element will
   * play the correct audio stream for the given session id.
   *
   * @param audioElement the audio element to bind to.
   * @param sessionId the session id.
   * @param trackType the kind of audio.
   */
  bindAudioElement = (
    audioElement: HTMLAudioElement,
    sessionId: string,
    trackType: AudioTrackType = 'audioTrack',
  ) => {
    const unbind = this.dynascaleManager?.bindAudioElement(
      audioElement,
      sessionId,
      trackType,
    );

    if (!unbind) return;
    this.audioBindingsWatchdog?.register(audioElement, sessionId, trackType);
    const cleanup = () => {
      unbind();
      this.audioBindingsWatchdog?.unregister(sessionId, trackType);
    };
    this.leaveCallHooks.add(cleanup);
    return () => {
      this.leaveCallHooks.delete(cleanup);
      cleanup();
    };
  };

  /**
   * Plays all audio elements blocked by the browser's autoplay policy.
   * Must be called from within a user gesture (e.g., click handler).
   *
   * Subscribe to `call.blockedAudioTracker.autoplayBlocked$` to know when a
   * gesture is required.
   */
  resumeAudio = () => this.blockedAudioTracker.resumeAudio();

  /**
   * Binds a DOM <img> element to this call's thumbnail (if enabled in settings).
   *
   * @param imageElement the image element to bind to.
   * @param opts options for the binding.
   */
  bindCallThumbnailElement = (
    imageElement: HTMLImageElement,
    opts: {
      fallbackImageSource?: string;
    } = {},
  ) => {
    const handleError = () => {
      imageElement.src =
        opts.fallbackImageSource ||
        'https://getstream.io/random_svg/?name=x&id=x';
    };

    const unsubscribe = createSubscription(
      this.state.thumbnails$,
      (thumbnails) => {
        if (!thumbnails) return;
        imageElement.addEventListener('error', handleError);

        const thumbnailUrl = new URL(thumbnails.image_url);
        thumbnailUrl.searchParams.set('w', String(imageElement.clientWidth));
        thumbnailUrl.searchParams.set('h', String(imageElement.clientHeight));

        imageElement.src = thumbnailUrl.toString();
      },
    );

    return () => {
      unsubscribe();
      imageElement.removeEventListener('error', handleError);
    };
  };

  /**
   * Specify preference for incoming video resolution. The preference will
   * be matched as close as possible, but actual resolution will depend
   * on the video source quality and client network conditions. Will enable
   * incoming video, if previously disabled.
   *
   * @param resolution preferred resolution, or `undefined` to clear preference
   * @param sessionIds optionally specify session ids of the participants this
   * preference has effect on. Affects all participants by default.
   */
  setPreferredIncomingVideoResolution = (
    resolution: VideoDimension | undefined,
    sessionIds?: string[],
  ) => {
    this.trackSubscriptionManager.setOverrides(
      resolution
        ? {
            enabled: true,
            dimension: resolution,
          }
        : undefined,
      sessionIds,
    );
    this.trackSubscriptionManager.apply();
  };

  /**
   * Enables or disables incoming video from all remote call participants,
   * and removes any preference for preferred resolution.
   */
  setIncomingVideoEnabled = (enabled: boolean) => {
    this.trackSubscriptionManager.setOverrides(
      enabled ? undefined : { enabled: false },
    );
    this.trackSubscriptionManager.apply();
  };

  /**
   * Sets the maximum amount of time a user can remain waiting for a reconnect
   * after a network disruption
   * @param timeoutSeconds Timeout in seconds, or 0 to keep reconnecting indefinetely
   */
  setDisconnectionTimeout = (timeoutSeconds: number) => {
    this.disconnectionTimeoutSeconds = timeoutSeconds;
  };

  /**
   * Configures the rolling-window limit for REJOIN/MIGRATE attempts. Once
   * `maxAttempts` rejoins have been registered inside `windowSeconds`, the
   * SDK stops retrying and transitions the call to `LEFT` with the
   * `rejoin_attempt_limit_exceeded` leave message.
   *
   * Defaults: 10 attempts per 120 seconds (aligned with the Swift SDK).
   * Both arguments are clamped to a minimum of 1. Unless this method is
   * called, an additional cap of 20 REJOIN attempts per 30 minutes applies.
   */
  setRejoinAttemptLimit = (maxAttempts: number, windowSeconds: number) => {
    this.rejoinLongWindowRateLimiter = undefined;
    this.rejoinRateLimiter.setLimits(
      Math.max(1, maxAttempts),
      Math.max(1, windowSeconds) * 1000,
    );
  };

  /**
   * Configures how many peer-connection failures where ICE never reached
   * `connected`/`completed` are tolerated before the SDK concludes that the
   * current network cannot support WebRTC and transitions the call to
   * `LEFT` with the `webrtc_unsupported_network` leave message.
   *
   * Default: 2. Clamped to a minimum of 1.
   */
  setMaxIceFailuresWithoutConnect = (n: number) => {
    this.maxIceFailuresWithoutConnect = Math.max(1, n);
  };

  /**
   * Configures how many consecutive SDP `NegotiationError`s are tolerated
   * before the SDK stops retrying and transitions the call to `LEFT` with
   * the `repeated_negotiation_failures` leave message.
   *
   * Default: 3. Clamped to a minimum of 1.
   */
  setMaxConsecutiveNegotiationFailures = (n: number) => {
    this.maxConsecutiveNegotiationFailures = Math.max(1, n);
  };

  /**
   * Enables the provided client capabilities.
   */
  enableClientCapabilities = (...capabilities: ClientCapability[]) => {
    for (const capability of capabilities) {
      this.clientCapabilities.add(capability);
    }
  };

  /**
   * Disables the provided client capabilities.
   */
  disableClientCapabilities = (...capabilities: ClientCapability[]) => {
    for (const capability of capabilities) {
      this.clientCapabilities.delete(capability);
    }
  };
}
